/**
 * BijSmaak - PDP round 1, MVP item 1: vertical mobile gallery.
 *
 * WHY THIS FILE EXISTS AND MUST STAY OUT OF theme.js
 * --------------------------------------------------
 * assets/theme.js is 208KB of minified vendor code. A theme update overwrites it
 * without warning and anything added there disappears silently. Custom behaviour
 * lives here instead. See 4-channels/cro/CLAUDE.md.
 *
 * WHAT BREAKS WITHOUT THIS FILE
 * -----------------------------
 * Below 700px the CSS turns the gallery into a fixed-height vertical scroll
 * container. The theme's ScrollCarousel is horizontal by construction: select()
 * computes a targetScrollLeft and calls scrollTo({left}). So in vertical mode
 * four things stop working, and this file restores exactly those four:
 *
 *   1. clicking a thumbnail does not move the gallery
 *   2. the active thumbnail never updates while scrolling
 *   3. a variant change does not scroll to that variant's image
 *   4. the lightbox opens on the wrong image
 *
 * WHY IT DOES NOT REUSE THE THEME'S OWN VERTICAL PATH
 * ---------------------------------------------------
 * ProductGalleryNavigation already has one, used by the desktop `grid` layout.
 * It is gated on `carousel.isScrollable` being false and it decides direction
 * from `window.scrollY`. A scroll CONTAINER never changes window.scrollY, so
 * that path reports the wrong direction here. We leave it switched off (our
 * container genuinely is scrollable, so it returns early on its own) and own
 * the active state instead. Nothing is monkey-patched on the prototype; only
 * per-instance properties are set, so the theme's own behaviour is untouched
 * above 700px.
 *
 * THE BREAKPOINT APPLIES TO THE GALLERY ONLY, SINCE 2026-08-18 evening.
 * ---------------------------------------------------------------------
 * `SizeDrawer` further down used to be mobile-only too, because desktop was the
 * control's page. Round 1.9 gave desktop the same design, so its button label,
 * its stock line, its sold-out handling and its refusal to add an unchosen size
 * all run at every width now. Only the vertical scroll container is a phone
 * mechanism, and only that still asks `matchMedia`.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 999px)';
  var DESKTOP = '(min-width: 1000px)';
  var SETTLE_MS = 700;
  // How far the visitor may scroll before the white band under the pinned CTA
  // fades. Randy 2026-08-20: "as soon as someone scrolls a bit, it gets removed."
  var TOP_BAND_PX = 24;
  // Round 2 (2026-09-25): the air between the price line and the top of the pinned
  // button on the landing screen. Part of the collapsed panel `sizeFrame` measures.
  var PANEL_GAP_PX = 12;
  // Air under the last USP line when the lock rests there (the stylesheet's
  // scroll-margin on the reassurance block says the same number).
  var LOCK_PAD_PX = 12;
  // The header is 60px tall; below this line of the panel's top edge more than
  // half of it is over the panel, and the header reads as over white.
  var OVER_PHOTO_PX = 30;

  /**
   * THE LOCK IS OFF WHILE THE PAGE MOVES ON A TAP, UNTIL THE NEXT GESTURE.
   * Randy's phone, 2026-09-27 12:42, on the live theme: a tap on the handle
   * scrolled the page to the rest point and Safari pulled it straight back to
   * the top. A programmatic scroll and the browser's own snapping fight over
   * the same page, and the browser wins. So `goToBuyingBlock` takes the class
   * off before it scrolls and nothing puts it back until the visitor's own
   * finger or wheel moves (`touchstart`, `wheel`, `pointerdown`), which is when
   * snapping is wanted again. Armed once at start, never on a scroll event.
   */
  var Lock = {
    suspended: false,
    /**
     * THE LOCK RETIRES AT THE FIRST SIZE TAP, FOR THE LIFE OF THE PAGE.
     * Randy's recording, 2026-09-28 13:04, on the live theme: a tap on a size
     * sent the page back to the top, and so did the tap that let the size go.
     * `<product-rerender>` rebuilds the whole column on a variant change, so the
     * block the snap engine was resting on is removed and re-added within one
     * frame, and iOS re-snaps to the only point it still knows: the top. Turning
     * the rest points off once a size is chosen (27 Sep) was not enough, because
     * the page was still a snap container. The lock is a landing behaviour; once
     * the visitor is choosing sizes the page scrolls freely until the next page
     * load. Never re-armed by a gesture: `wanted` says no from here on.
     */
    retired: false,
    wanted: function () {
      return !this.retired && !navigator.webdriver && window.matchMedia('(max-width: 999px)').matches;
    },
    retire: function () {
      this.retired = true;
      this.disarm();
    },
    arm: function () {
      if (this.suspended || !this.wanted()) return;
      document.documentElement.classList.add('pdp-lock');
    },
    disarm: function () {
      document.documentElement.classList.remove('pdp-lock');
    },
    suspend: function () {
      var self = this;
      this.suspended = true;
      this.disarm();
      if (this.bound) return;
      this.bound = true;
      var rearm = function () {
        self.suspended = false;
        self.bound = false;
        ['touchstart', 'wheel', 'pointerdown'].forEach(function (type) {
          document.removeEventListener(type, rearm, true);
        });
        self.arm();
      };
      ['touchstart', 'wheel', 'pointerdown'].forEach(function (type) {
        document.addEventListener(type, rearm, { capture: true, passive: true });
      });
    }
  };

  function VerticalGallery(gallery) {
    this.gallery = gallery;
    this.carousel = gallery.querySelector('.product-gallery__carousel');
    if (!this.carousel) return;
    this.ready = true;

    this.index = 0;
    this.isProgrammaticScroll = false;
    this.isEnabled = false;
    this.settleTimer = null;
    this.handleBound = false;
    this.isTouching = false;

    this.onScroll = this.onScroll.bind(this);
    this.onCaptureClick = this.onCaptureClick.bind(this);

    // Tap-to-enlarge and the lightbox are not phone mechanisms any more (see
    // installLightbox below), so both are bound here rather than in enable().
    // Capture phase, so we run before the theme's own click handler.
    this.gallery.addEventListener('click', this.onCaptureClick, true);
    this.installLightbox();

    this.mediaQuery = window.matchMedia(MOBILE);
    this.mediaQuery.addEventListener('change', this.sync.bind(this));
    this.sync();
  }

  /** Cells the visitor can actually reach. Variant filtering hides the rest. */
  VerticalGallery.prototype.cells = function () {
    return Array.prototype.filter.call(this.carousel.children, function (cell) {
      return !cell.hasAttribute('hidden');
    });
  };

  /** Thumbnails and dots alike. Both render as direct button children. */
  VerticalGallery.prototype.navGroups = function () {
    var groups = [];
    this.gallery
      .querySelectorAll('product-gallery-navigation, carousel-navigation')
      .forEach(function (nav) {
        var buttons = Array.prototype.filter.call(nav.children, function (el) {
          return el.tagName === 'BUTTON' && !el.hasAttribute('hidden');
        });
        if (buttons.length) groups.push(buttons);
      });
    return groups;
  };

  VerticalGallery.prototype.sync = function () {
    if (this.mediaQuery.matches) this.enable();
    else this.disable();
  };

  VerticalGallery.prototype.enable = function () {
    if (this.isEnabled) return;
    this.isEnabled = true;

    // `adaptive-height` resizes the container to whichever cell is selected. Right
    // for a horizontal carousel, exactly wrong here: the frame is a fixed 4:5
    // window and the peek at the next photo is the whole affordance.
    //
    // MEASURED 2026-08-17 at a true 390px viewport: the theme writes
    // `max-height: <cell height>px` as an INLINE style, which beat the stylesheet
    // and collapsed the 487px frame to one 298px photo. Removing the attribute is
    // NOT enough, because the theme binds its carousel:settle listener once at
    // connect time and never re-reads the attribute. So the inline value is
    // cleared after every dispatch instead.
    this.carousel.removeAttribute('adaptive-height');
    this.clearAdaptiveHeight();

    this.carousel.addEventListener('scroll', this.onScroll, { passive: true });

    // THE signal that separates a flick from a drag. iOS only runs momentum after
    // the finger has left the glass, so "no touch down" is what tells us the
    // gallery is gliding rather than being pushed. Velocity alone cannot do it:
    // a deliberate drag and a flick overlap far too much.
    var touchTracker = this;
    this.onTouchStart = function () { touchTracker.isTouching = true; };
    this.onTouchEnd = function () { touchTracker.isTouching = false; };
    this.carousel.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.carousel.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.carousel.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

    // Page-level: the instant the finger lifts, pick a state. Bound to the
    // document rather than the gallery because the drag that moves between states
    // usually starts on the info block, not on the photos.
    this.onPageTouchEnd = function () {
      touchTracker.isTouching = false;
    };
    document.addEventListener('touchend', this.onPageTouchEnd, { passive: true });
    document.addEventListener('touchcancel', this.onPageTouchEnd, { passive: true });

    // An IntersectionObserver rooted on the frame is the PRIMARY trigger; the
    // scroll listener above is the backup. Two reasons it is this way round:
    // iOS throttles scroll events hard during momentum scrolling, and the
    // observer is driven by layout rather than by event delivery, so it also
    // catches photos being filtered in or out by a variant change.
    if (window.IntersectionObserver) {
      var self = this;
      this.observer = new IntersectionObserver(
        function () {
          self.onScroll();
        },
        { root: this.carousel, threshold: [0, 0.25, 0.5, 0.75, 1] }
      );
      this.cells().forEach(function (cell) {
        self.observer.observe(cell);
      });
    }
    var handle = this.gallery.querySelector('.pdp-vgallery-handle');
    if (handle && !this.handleBound) {
      this.handleBound = true;
      var self = this;

      // The drag gesture went with the states. A tap is unambiguous; a drag had
      // to be told apart from a scroll, which is the guessing this build is
      // getting out of.
      handle.addEventListener('click', function () {
        self.goToBuyingBlock();
      });
    }

    this.setActive(0);

    var sizer = this;
    var resize = function () { sizer.sizeFrame(); sizer.fitPackshots(); };
    if (!this.onFitLoad) {
      this.onFitLoad = function (event) {
        if (event.target && event.target.tagName === 'IMG' && event.target.closest('.product-gallery__media--fit')) sizer.fitPackshots();
      };
    }
    this.carousel.addEventListener('load', this.onFitLoad, true);
    this.fitPackshots();
    this.onViewportChange = resize;
    // 2026-10-03 (Gaetano's screen recording at the bottom of a product page): on a phone the browser's toolbar
    // grows and shrinks while scrolling, and every time that fired a resize, the photo was sized again and the
    // active photo and its tone were read again, and the logo blinked black over a dark photo. The photo is sized in
    // svh, which ignores the toolbar, so only a new WIDTH (a rotation) is a reason to measure again here.
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      resize();
    });
    window.addEventListener('orientationchange', resize);
    // Round 2: the collapsed panel is measured off the title and price blocks, and
    // <product-rerender> replaces both on a variant change. Images settle last.
    window.addEventListener('load', resize);
    document.addEventListener('product:rerender', resize, true);
    // Once after layout, and once more after webfonts land, because a font swap
    // is exactly what turns a one-line product name into two.
    requestAnimationFrame(resize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(resize);
    setTimeout(resize, 600);
  };

  VerticalGallery.prototype.disable = function () {
    if (!this.isEnabled) return;
    this.isEnabled = false;

    this.carousel.removeEventListener('scroll', this.onScroll);
    this.carousel.removeEventListener('touchstart', this.onTouchStart);
    this.carousel.removeEventListener('touchend', this.onTouchEnd);
    this.carousel.removeEventListener('touchcancel', this.onTouchEnd);
    if (this.onPageScroll) window.removeEventListener('scroll', this.onPageScroll);
    if (this.onPageTouchEnd) {
      document.removeEventListener('touchend', this.onPageTouchEnd);
      document.removeEventListener('touchcancel', this.onPageTouchEnd);
    }
    if (this.onViewportChange) {
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('orientationchange', this.onViewportChange);
      window.removeEventListener('load', this.onViewportChange);
      document.removeEventListener('product:rerender', this.onViewportChange, true);
    }
    var closedSection = this.gallery.closest('.shopify-section');
    if (closedSection) closedSection.style.removeProperty('--pdp-state1-reserve');
    if (this.onFitLoad) this.carousel.removeEventListener('load', this.onFitLoad, true);
    this.gallery.classList.remove('pdp-vgallery--single');
    this.gallery.querySelectorAll('.product-gallery__media--fit').forEach(function (cell) {
      var image = cell.querySelector(':scope > img');
      if (image) { image.style.removeProperty('transform'); image.style.removeProperty('transform-origin'); }
      cell.style.removeProperty('background-color');
    });

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.carousel.setAttribute('adaptive-height', '');
  };

  /**
   * ==========================================================================
   * THE LIGHTBOX IS OURS, AT EVERY WIDTH, AND IT CARRIES THE VIDEO. 2026-09-03.
   * ==========================================================================
   * Randy, on his phone, 2026-09-03: the video "can't be opened in preview
   * (when you click it it won't open in 9:16)", and swiping through the
   * enlarged photos "skips the video".
   *
   * The theme's openLightBox() builds PhotoSwipe's slides from the `<img>`
   * elements that are DIRECT children of a cell and indexes them against image
   * cells only. A video cell's poster sits inside `<video-media>`, so the video
   * contributed no slide and a tap on it had nowhere to go. (Until today this
   * file only re-mapped the index for that image-only list.)
   *
   * This override builds the slides from every visible cell, in order: a photo
   * exactly as the theme did it (same object shape, so `thumbCropped` and the
   * zoom animation keep working), a video as a PhotoSwipe `html` slide holding
   * a fresh `<video>` at the file's own 9:16 - controls on, muted, playing on
   * arrival, paused on leave. Indexes are CELL indexes throughout, and the
   * capture click handler is the only thing that produces them: it stops
   * propagation, so the theme's own click handler, which speaks image indexes,
   * never runs and never opens a second box.
   *
   * It uses the theme's own PhotoSwipe instance (`gallery.lightBox`), so the
   * close/prev/next buttons, the strings and the styling stay the theme's.
   * `loadAndOpen(index, dataSource)` is PhotoSwipe's public API.
   *
   * Why the inline clip has no controls and this one does - Randy, 2026-09-03:
   * "the black overlay and pause button are visible when you scroll down, which
   * shouldn't be the case." iOS draws its control bar over any playing inline
   * video that carries `controls`. Inline is a silent loop; the tap is the
   * deliberate step, and the lightbox is where sound and pause live.
   */
  VerticalGallery.prototype.installLightbox = function () {
    if (typeof this.gallery.openLightBox !== 'function' || this.gallery.bijsmaakLightbox) return;
    this.gallery.bijsmaakLightbox = true;

    var self = this;
    this.gallery.openLightBox = function (index) {
      self.openLightBoxAt(index);
    };
  };

  function attr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  /** One entry per visible cell: { cell, slide }. `slide` is null for media the lightbox cannot show (3D). */
  VerticalGallery.prototype.lightboxSource = function () {
    return this.cells().map(function (cell) {
      var type = cell.getAttribute('data-media-type');

      if (type === 'video') {
        var inline = cell.querySelector('video');
        var source = inline && inline.querySelector('source');
        var src = source ? source.getAttribute('src') : inline && inline.getAttribute('src');
        if (!src) return { cell: cell, slide: null };

        var poster = cell.querySelector('video-media > img');
        var posterSrc = poster ? poster.currentSrc || poster.src : inline.getAttribute('poster');
        var width = poster ? parseInt(poster.getAttribute('width'), 10) : 0;
        var height = poster ? parseInt(poster.getAttribute('height'), 10) : 0;

        return {
          cell: cell,
          slide: {
            html:
              '<div class="pdp-lightbox-video"><video src="' + attr(src) + '"' +
              (posterSrc ? ' poster="' + attr(posterSrc) + '"' : '') +
              ' aria-label="' + attr(inline.getAttribute('aria-label')) + '"' +
              ' playsinline muted loop controls preload="metadata"></video></div>',
            width: width || 1080,
            height: height || 1920,
            thumbnailElement: poster || undefined
          }
        };
      }

      var image = cell.querySelector(':scope > img');
      if (!image) return { cell: cell, slide: null };

      return {
        cell: cell,
        slide: {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width'), 10),
          height: parseInt(image.getAttribute('height'), 10),
          alt: image.alt,
          thumbCropped: true
        }
      };
    });
  };

  VerticalGallery.prototype.openLightBoxAt = function (index) {
    var entries = this.lightboxSource();

    if (index == null) {
      // The zoom icon path. Ours on the phone; the theme's selected cell elsewhere.
      var cells = this.cells();
      index = this.isEnabled ? this.index : Math.max(0, cells.indexOf(this.carousel.selectedCell));
    }

    var slides = [];
    var target = 0;
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].slide) continue;
      if (i === index) target = slides.length;
      slides.push(entries[i].slide);
    }
    if (!slides.length) return;

    var box = this.gallery.lightBox;
    if (!box || typeof box.loadAndOpen !== 'function') return;
    this.bindLightboxVideo(box);
    box.loadAndOpen(target, slides);
  };

  /**
   * Play the video slide when it becomes the current one, pause it when it
   * stops being that, and pause everything on close. Bound once per PhotoSwipe
   * instance; `on()` registers on the lightbox and is forwarded to every
   * PhotoSwipe it opens. `contentActivate` / `contentDeactivate` are PhotoSwipe
   * 5 events and carry the slide's content, whose `element` is our markup.
   */
  VerticalGallery.prototype.bindLightboxVideo = function (box) {
    if (box.bijsmaakVideoBound || typeof box.on !== 'function') return;
    box.bijsmaakVideoBound = true;

    function videoIn(event) {
      var element = event && event.content && event.content.element;
      return element ? element.querySelector('video') : null;
    }

    box.on('contentActivate', function (event) {
      var video = videoIn(event);
      if (!video) return;
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    });

    box.on('contentDeactivate', function (event) {
      var video = videoIn(event);
      if (video) video.pause();
    });

    box.on('close', function () {
      var pswp = box.pswp;
      if (!pswp || !pswp.element) return;
      pswp.element.querySelectorAll('video').forEach(function (video) {
        video.pause();
      });
    });
  };

  VerticalGallery.prototype.onCaptureClick = function (event) {
    var button = event.target.closest('button');
    if (button && !this.isEnabled) return;
    if (button) {
      var groups = this.navGroups();
      for (var g = 0; g < groups.length; g++) {
        var position = groups[g].indexOf(button);
        if (position === -1) continue;

        // Stop the theme's handler, which would compute a scrollLeft here.
        event.preventDefault();
        event.stopPropagation();
        this.select(position);
        return;
      }
      return;
    }

    // Tap the photo, or the video, to enlarge it. The theme has the photo half
    // but gates it on `matchesMediaQuery("md")` and on the IMG tag, so on a
    // phone only the (now hidden) zoom icon worked and a video never did. A tap
    // anywhere in a video cell counts, including the theme's shadow play
    // button, whose click is retargeted to the <video-media> host.
    if (!this.gallery.hasAttribute('allow-zoom')) return;

    var cell = event.target.closest('.product-gallery__media');
    if (!cell) return;
    var type = cell.getAttribute('data-media-type');
    if (type === 'image' && event.target.tagName !== 'IMG') return;
    if (type !== 'image' && type !== 'video') return;

    var position = this.cells().indexOf(cell);
    if (position === -1) return;

    // Stop the theme's handler: it speaks image indexes and would open a
    // second box on desktop. It also stops <video-media>'s own click-to-play.
    event.preventDefault();
    event.stopPropagation();
    this.openLightBoxAt(position);
  };

  /**
   * Scroll the PAGE so the buying block sits under the header.
   *
   * This is the handle's whole job, and it is also the honest answer to "the
   * gallery traps my scroll". A scroll container cannot hand a single finger
   * gesture over to the page on iOS: the visitor has to lift and swipe again.
   * Miu Miu does not solve that either, they lock the page entirely and give you
   * this handle as the way out. So we give the way out.
   */
  VerticalGallery.prototype.buyingBlock = function () {
    return (
      document.querySelector('.product-info') ||
      this.gallery.closest('.product') ||
      this.gallery.parentElement
    );
  };

  /**
   * Is the gallery still meaningfully on screen?
   *
   * This is what makes the handle a toggle rather than a one-way trip. Measured
   * against the gallery rather than against the buying block on purpose: the
   * buying block is often taller than the screen, so "is it fully in view" is
   * never true and the handle would jam pointing downward.
   */
  VerticalGallery.prototype.isGalleryInView = function () {
    return this.gallery.getBoundingClientRect().bottom > 120;
  };

  /**
   * ------------------------------------------------------------------------
   * THE PAGE NEVER MOVES ON ITS OWN ANY MORE
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18, after four versions of this: "what if we let go of all
   * the states and scrolling. Seems to be difficult."
   *
   * He is right, and the reason every version failed is the same. Each one had
   * to guess an intention from a gesture - which way you meant to go, whether
   * you were done, whether a glide was a flick or a drag - and a wrong guess is
   * worse than no guess at all, because the visitor loses their place. There is
   * no reading of a touch event that is right often enough for that trade.
   *
   * Deleted with the states: an eased page scroll, a direction-tracking snap on
   * touchend, a state lock, a CSS snap barrier gated on a class, two
   * `scroll-snap-stop` points, and the momentum hand-off out of the gallery.
   *
   * What is left is the one movement a visitor asks for explicitly: TAPPING the
   * divider, or the button while the photos are still on screen, scrolls to the
   * sizes. Ordinary `scrollIntoView`, no easing of ours, no state.
   */
  /**
   * TAPPING THE DIVIDER LEAVES NO PHOTO ON SCREEN.
   *
   * Randy 2026-08-18: "when clicking the divider the image is still visible at
   * top, should be clean."
   *
   * It scrolled `.product-info` to the top, and that block starts BELOW the
   * handle, so the browser stopped a little short and left a strip of the last
   * photo above it.
   *
   * ---------------------------------------------------------------------------
   * THE OFFSET IS THE CAROUSEL'S BOTTOM EDGE, NOT THE HEADER'S HEIGHT.
   * ---------------------------------------------------------------------------
   * **Randy 2026-08-20, on his phone: "the divider should be the top, not the
   * image."** The first version reserved the header's height above the handle so
   * the divider would sit directly under a sticky header. `hide_on_scroll` was
   * switched on in round 1.9, so on a real downward scroll the header is GONE by
   * the time the page settles - and the 57px it had reserved is filled by the
   * bottom of the last photograph. Exactly the defect this function exists to
   * prevent, reintroduced by the fix for it.
   *
   * **THE HARNESS COULD NOT HAVE CAUGHT THIS AND THAT IS WORTH KEEPING.**
   * `scrollTo` does not trigger `hide-on-scroll` - a real wheel or touch scroll
   * does - so the header is still there when the measurement is taken and the
   * landing reads as correct. Same family as "programmatic scroll delivers no
   * scroll callbacks". A landing that depends on the header's behaviour is a
   * phone question.
   *
   * So the page lands on a distance this file can measure instead: the gap
   * between the bottom of the last photo and the top of the handle, which puts
   * the carousel's bottom edge exactly on the top of the viewport. No photograph
   * can show above the divider whether the header is there or not.
   */
  /**
   * ROUND 2, 2026-09-25: THE HANDLE AND THE BUTTON GO TO THE LOCK'S SECOND POINT.
   *
   * The page has two rest positions now (plan item 6): the top, and "the panel
   * up to the button", which is the end of the size advice plus everything that
   * follows it in the flow, aligned to the bottom of the screen. The CSS snap
   * point is defined the same way (`--pdp-lock-tail` in the gallery snippet), so
   * a tap lands exactly where a flick would settle. Read off the page, never a
   * number: the size advice is two lines on one product and three on another.
   *
   * The button's OWN height rather than its block's: once a size is chosen the
   * block grows by the express-checkout button, and the rest position must not
   * move under a visitor who is already in the sizes.
   */
  VerticalGallery.prototype.lockTarget = function () {
    var section = this.gallery.closest('.shopify-section');
    var note = section && section.querySelector('.product-info__block-item[data-block-id="liquid_fit_note"]');
    var buy = section && section.querySelector('.product-info__block-item[data-block-id="buy_buttons"]');
    if (!note || !buy) return null;

    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var viewport = window.innerHeight || document.documentElement.clientHeight;

    // The USP rest position, when the panel top stays in view (see sizeFrame).
    if (section.classList.contains('pdp-lock-usps')) {
      var usps = section.querySelector('.product-info__block-item[data-block-id="liquid_reassurance"]');
      if (usps) {
        return Math.max(0, Math.round(usps.getBoundingClientRect().bottom + y + LOCK_PAD_PX - viewport));
      }
    }

    var noteBottom = note.getBoundingClientRect().bottom + y;
    var tail = parseFloat(window.getComputedStyle(note).marginBottom) || 0;
    var button = buy.querySelector('.buy-buttons button, button');
    var buttonHeight = button ? button.getBoundingClientRect().height : 0;
    var offset = parseFloat(window.getComputedStyle(buy).bottom);
    if (isNaN(offset)) offset = 0;

    return Math.max(0, Math.round(noteBottom + tail + buttonHeight + offset - viewport));
  };

  VerticalGallery.prototype.goToBuyingBlock = function () {
    var target = this.lockTarget();
    Lock.suspend();
    if (target === null) {
      var block = this.buyingBlock();
      if (block) block.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  /** Keep the screen-reader label honest about which way the handle will go. */
  VerticalGallery.prototype.syncHandleLabel = function () {
    var handle = this.gallery.querySelector('.pdp-vgallery-handle');
    if (!handle) return;
    handle.setAttribute('aria-label', 'Ga naar maat en bestellen');
  };

  VerticalGallery.prototype.select = function (index) {
    var cell = this.cells()[index];
    if (!cell) return;

    this.setActive(index);

    // scrollTo on the container rather than cell.scrollIntoView, which would
    // also scroll the page to bring the container into view.
    this.isProgrammaticScroll = true;
    this.carousel.scrollTo({ top: cell.offsetTop, behavior: 'smooth' });

    clearTimeout(this.settleTimer);
    var self = this;
    this.settleTimer = setTimeout(function () {
      self.isProgrammaticScroll = false;
    }, SETTLE_MS);
  };

  /**
   * Computed synchronously, on purpose.
   *
   * This was throttled through requestAnimationFrame and the active dot stopped
   * following the scroll: one callback failed to run, the in-flight flag was
   * never cleared, and every later scroll returned early forever. The work here
   * is a loop over a handful of cells, so the throttle was buying nothing and
   * costing a whole class of stuck-state bug.
   */
  /**
   * Active photo = the one filling most of the frame.
   *
   * Randy asked for Miu Miu's rule, "as soon as a bit of the next photo is in
   * view, the dot moves". That rule cannot be copied literally here, because
   * their frame fits about one photo and ours deliberately shows the top of the
   * next one at rest. Taken literally, photo 2 would be active the moment the
   * page loaded and photo 1 would never be current at all.
   *
   * Largest-visible-area is the same intent expressed for our frame: it answers
   * "which photo am I looking at", it behaves exactly like theirs when a photo
   * fills the frame, and the dot moves as soon as the next photo takes over.
   *
   * Computed synchronously, and not throttled through requestAnimationFrame. It
   * was, and one dropped callback left the in-flight flag set so every later
   * scroll returned early forever. The loop is over a handful of cells.
   */
  VerticalGallery.prototype.onScroll = function () {
    if (this.isProgrammaticScroll) return;

    var cells = this.cells();
    var frameTop = this.carousel.scrollTop;
    var frameBottom = frameTop + this.carousel.clientHeight;
    var winner = 0;
    var largestVisible = -1;

    for (var i = 0; i < cells.length; i++) {
      var cellTop = cells[i].offsetTop;
      var cellBottom = cellTop + cells[i].offsetHeight;
      var visible = Math.min(cellBottom, frameBottom) - Math.max(cellTop, frameTop);

      if (visible > largestVisible) {
        largestVisible = visible;
        winner = i;
      }
    }

    if (winner !== this.index) this.setActive(winner);
    else this.toneFromTop();
  };

  /**
   * THE MOMENTUM HAND-OFF WAS HERE AND IT IS GONE.
   *
   * It carried a flick out of the gallery and on into the buying block, which
   * is genuinely what Miu Miu does. It also moved the page without being asked,
   * which is the category of behaviour Randy asked to remove on 2026-08-18.
   *
   * The trap it existed to solve is real and is now answered differently: the
   * frame is 4:5, so about a third of the screen below it is ordinary page that
   * scrolls normally, and tapping the divider goes to the sizes.
   */

  /**
   * ------------------------------------------------------------------------
   * THE FRAME IS AS TALL AS 4:5 OR AS TALL AS THE PRICE ALLOWS, WHICHEVER IS LESS
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18: "the price should be shown in the first state (we have the
   * room for it). So it's product name, price, CTA."
   *
   * This was retired earlier the same day, when nothing had to fit anything. It
   * comes back because that instruction puts a real constraint on the photo
   * again: the price sits above a buy button that is pinned near the bottom of
   * the screen, so the photo can only be as tall as what is left.
   *
   * WHY IT IS MEASURED RATHER THAN A NUMBER IN THE STYLESHEET. The product name
   * is one line on most products and two on a long one - "Hi-Tec HTS Shadow RGS
   * star white winter twig dawn blue mangano calcite gum" is 74 characters. A
   * fixed reserve has to assume the worst case, which costs every short-named
   * product 23px of photo for a long-named one's benefit. Measured at 390x745, a
   * fixed 300px reserve left a two-line name TWO pixels clear of the button.
   *
   * So the reserve is read off the page: header, name, price, the gaps between
   * them, the button block and the band under it, plus ten pixels of slack. Every
   * product then gets the same clearance and the tallest photo that allows it.
   *
   * **This measures LAYOUT, never a gesture.** That distinction is the whole
   * lesson of 2026-08-18: four scroll mechanisms were built and deleted because
   * each had to guess an intention from a touch. Element heights are facts.
   *
   * The stylesheet still caps the result at 125vw, so a tall phone gets a true
   * 4:5 and this only ever gives height back.
   */
  /**
   * THE FRAME HEIGHT IS A NUMBER IN THE STYLESHEET NOW, NOT A MEASUREMENT.
   *
   * Four versions of this function reserved exactly what the stack below the
   * photo needed, because something under it - a second state, a sticky button,
   * a price - had to be guaranteed a place on the first screen. Nothing does any
   * more (Randy 2026-08-18: "Just 1 PDP with all in it"), so the photo is a shape
   * and `--pdp-frame-height` in `snippets/product-gallery.liquid` says what it is.
   *
   * Kept as a no-op that CLEARS anything an earlier version left behind, because
   * the variable it used to write outlives a cached page.
   */
  /**
   * ROUND 2, 2026-09-25: THE FRAME IS THE SCREEN MINUS THE COLLAPSED PANEL, AND
   * THE PANEL IS MEASURED. The collapsed panel is the handle, the brand, the
   * model, the price line and the pinned button with its offset. A product name
   * is one line on most products and three on some, so a fixed number would cost
   * every short-named product photo for a long-named one's benefit (the August
   * argument, unchanged). `--pdp-panel-collapsed` goes on the section and the
   * stylesheet subtracts it from `100svh`.
   *
   * This measures LAYOUT, never a gesture. Element heights are facts.
   *
   * The button's OWN height, not its block's, for the reason in lockTarget: the
   * express-checkout button appears on a size click and must not resize the photo.
   * A sold-out product hides the button block; the panel then ends at the price.
   */
  VerticalGallery.prototype.sizeFrame = function () {
    var section = this.gallery.closest('.shopify-section');
    if (!section) return;
    section.style.removeProperty('--pdp-state1-reserve');

    if (!this.isEnabled) {
      section.style.removeProperty('--pdp-panel-collapsed');
      this.collapsed = null;
      return;
    }

    var info = section.querySelector('.product-info');
    var price = section.querySelector('.product-info__block-item[data-block-id="liquid_price_row"]');
    var buy = section.querySelector('.product-info__block-item[data-block-id="buy_buttons"]');
    if (!info || !price) return;

    var top = info.getBoundingClientRect().top;
    var priceBottom = price.getBoundingClientRect().bottom;

    var buttonHeight = 0;
    var offset = 0;
    if (buy && window.getComputedStyle(buy).display !== 'none') {
      var button = buy.querySelector('.buy-buttons button, button');
      buttonHeight = button ? button.getBoundingClientRect().height : 0;
      offset = parseFloat(window.getComputedStyle(buy).bottom);
      if (isNaN(offset)) offset = 0;
    }

    var collapsed = Math.ceil(priceBottom - top + PANEL_GAP_PX + buttonHeight + offset);
    if (collapsed > 0 && collapsed !== this.collapsed) {
      this.collapsed = collapsed;
      section.style.setProperty('--pdp-panel-collapsed', collapsed + 'px');
    }

    /**
     * WHERE THE LOCK'S SECOND POINT SITS (Randy 2026-09-25): at the USP list when
     * the panel from its top edge to the last USP line fits the screen, at the
     * button otherwise. One measurement of heights, written as a class the
     * stylesheet and lockTarget both read, so a tap and a flick agree.
     */
    var usps = section.querySelector('.product-info__block-item[data-block-id="liquid_reassurance"]');
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    var fits = false;
    if (usps) {
      var uspBottom = usps.getBoundingClientRect().bottom;
      fits = uspBottom - top + LOCK_PAD_PX <= viewport;
    }
    section.classList.toggle('pdp-lock-usps', fits);
  };


  /**
   * ------------------------------------------------------------------------
   * FORCE ONE OF THE TWO STATES
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18: "on miu miu you are either in one of the two, so when you
   * scroll half way, it picks for you. I also want that, feels cleaner."
   *
   * Done in JavaScript rather than with `scroll-snap-type: y mandatory` on the
   * page. Mandatory snapping applies to the WHOLE document, so it would also
   * fight every scroll past the description, the breadcrumb and the
   * recommendations - you could not rest anywhere below the buying block. This
   * only acts inside the band between the two states and leaves the rest of the
   * page alone.
   *
   * IT DECIDES THE MOMENT THE FINGER LEAVES THE GLASS, not when scrolling stops.
   * Randy 2026-08-18: "if I scroll and let go of my finger, the scroll continues
   * and then fades. only when fully stopped it snaps into place, but that still
   * feels too free. can we do that as soon as your finger leaves screen."
   *
   * Waiting for a settle meant riding out the whole momentum glide first, which
   * reads as the page drifting and then correcting itself. Acting on `touchend`
   * takes the decision while the glide is still running, so it reads as one
   * movement. The settle timer stays as the fallback for anything that scrolls
   * without touch, like a trackpad or a mouse wheel.
   */
  var SETTLE_DELAY_MS = 140;

  /**
   * ------------------------------------------------------------------------
   * THE STATE SNAPPING LIVES IN CSS NOW. THIS IS THE NOTE THAT REPLACED IT.
   * ------------------------------------------------------------------------
   *
   * There used to be a `snapToNearestState` here that ran on `touchend`, read
   * the scroll direction and scrolled the page to whichever state it decided on.
   * It is gone, and its two failure modes are worth keeping written down because
   * they are the same failure:
   *
   *   1. Getting back to the top was "too difficult, I have to swipe too hard".
   *   2. Coming up from the bottom skipped state 2 and went all the way to
   *      state 1 in one gesture.
   *
   * Both came from asking JavaScript to decide something it cannot see. iOS
   * throttles scroll events hard during momentum, so a flick from the
   * recommendations hands us a touchend far below state 2 and then almost
   * nothing until the glide has already passed it. The last version tried to
   * fix that by gating a CSS snap on a class this handler toggled, which only
   * moved the problem: during a fast swipe the class was stale, so the snap held
   * on when it should have released and released when it should have held.
   *
   * `snippets/product-gallery.liquid` now declares two snap points with
   * `scroll-snap-stop: always` - the gallery for state 1, the drag handle for
   * state 2 - and the browser enforces them on the compositor, where the
   * momentum is. No timing, no direction guessing, nothing to get stale.
   *
   * What is left in this file is only what a snap point cannot do: moving the
   * page when the handle or the state-1 button is TAPPED.
   */

  /**
   * THE TONE COMES FROM THE PHOTO BEHIND THE BAR, NOT FROM THE "ACTIVE" PHOTO (2026-10-05, found on the De Sluwe Vos x
   * Mizuno tee while checking Gaetano's line: a white logo and white icons over a white packshot). The active photo is
   * the one with the most of itself on screen, for the dots. A short landscape packshot followed by a tall dark photo
   * loses that count to the dark one while the packshot is the one under the bar, so the dark photo set the logo
   * white over white. The bar sits over the top of the frame, so the photo that covers the frame's top edge is read.
   * Read once per photo, not on every callback that re-confirms the same photo; a re-read could pass through the
   * "not read yet, so black" state and blink the logo (2026-10-03).
   */
  VerticalGallery.prototype.toneFromTop = function () {
    var cells = this.cells();
    if (!cells.length) return;
    var line = this.carousel.scrollTop + 32;
    var cell = cells[0];
    for (var i = 0; i < cells.length; i++) {
      if (cells[i].offsetTop <= line && cells[i].offsetTop + cells[i].offsetHeight > line) { cell = cells[i]; break; }
    }
    if (this.toneCell !== cell) {
      this.toneCell = cell;
      this.sampleTone(cell);
    }
  };

  /**
   * ONE SIZE: THE PACKSHOT AS BIG AS IT CAN BE, NEVER CUT (Gaetano 2026-10-05, Jason Markk Protect Spray, mock-up on
   * the canvas: "Ik vind die foto zo groot best wel vet"; "zo groot mogelijk, nooit afsnijden"). A product with only one
   * variant gets the same frame as a sneaker (`product-gallery__media--fit`, snippets/product-gallery.liquid) and its
   * photo sits in it whole (`object-fit: contain`). Here the photo is read once (the same 0.2 band trick as the tone,
   * but the whole image, small): where is the product on its background? Then the photo is enlarged around the
   * product until the frame is filled (as a cover would) OR the product would touch the frame's sides (92% of the
   * width) or its top and bottom (80% of the height, the bar sits over the top), whichever comes first, and the
   * product is put in the middle. A tall bottle grows to fill the frame; a wide set stays as wide as the screen and
   * keeps all of itself. The frame takes the colour of the photo's corner, so where the enlarged photo does not reach,
   * nothing shows. A photo that cannot be read keeps the plain contain: whole, just smaller. Phones only; desktop has
   * no frame and keeps the theme's own picture.
   */
  var fitCache = {};

  VerticalGallery.prototype.fitPackshots = function () {
    if (!this.isEnabled) return;
    var cells = this.gallery.querySelectorAll('.product-gallery__media--fit');
    for (var c = 0; c < cells.length; c++) {
      var cell = cells[c];
      var image = cell.querySelector(':scope > img');
      if (!image || !image.complete || !image.naturalWidth) continue;
      var frameW = cell.clientWidth;
      var frameH = cell.clientHeight;
      if (!frameW || !frameH) continue;

      var key = image.currentSrc || image.src;
      var box = fitCache[key];
      if (box === undefined) {
        box = null;
        try {
          var sw = 120;
          var sh = Math.max(1, Math.round(sw * image.naturalHeight / image.naturalWidth));
          var canvas = document.createElement('canvas');
          canvas.width = sw;
          canvas.height = sh;
          var context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, sw, sh);
          var data = context.getImageData(0, 0, sw, sh).data;
          var br = data[0], bg = data[1], bb = data[2];
          var x0 = sw, y0 = sh, x1 = -1, y1 = -1;
          for (var y = 0; y < sh; y++) {
            for (var x = 0; x < sw; x++) {
              var i = (y * sw + x) * 4;
              if (Math.abs(data[i] - br) + Math.abs(data[i + 1] - bg) + Math.abs(data[i + 2] - bb) > 36) {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
              }
            }
          }
          if (x1 >= x0 && y1 >= y0) {
            box = {
              x0: x0 / sw, x1: (x1 + 1) / sw, y0: y0 / sh, y1: (y1 + 1) / sh,
              colour: 'rgb(' + br + ', ' + bg + ', ' + bb + ')'
            };
          }
        } catch (error) {
          box = null;
        }
        fitCache[key] = box;
      }
      // Shown from here on (snippets/product-gallery.liquid keeps it see-through until now): enlarged, or, when the
      // photo could not be read, whole and small.
      if (!box) { cell.classList.add('is-fitted'); continue; }

      var w = image.naturalWidth;
      var h = image.naturalHeight;
      var contain = Math.min(frameW / w, frameH / h);
      var cover = Math.max(frameW / w, frameH / h);
      var productW = (box.x1 - box.x0) * w;
      var productH = (box.y1 - box.y0) * h;
      var scale = Math.min(cover, (frameW * 0.92) / productW, (frameH * 0.8) / productH);
      scale = Math.max(scale, contain);
      var grow = scale / contain;

      // Where the product's centre sits in the contained picture, and where it should go: the middle of the frame,
      // a little below the true middle because the bar covers the top.
      var offsetX = (frameW - w * contain) / 2;
      var offsetY = (frameH - h * contain) / 2;
      var centreX = offsetX + (box.x0 + box.x1) / 2 * w * contain;
      var centreY = offsetY + (box.y0 + box.y1) / 2 * h * contain;
      var moveX = frameW / 2 - centreX;
      var moveY = (frameH + 56) / 2 - centreY;

      image.style.transformOrigin = centreX + 'px ' + centreY + 'px';
      image.style.transform = 'translate(' + moveX.toFixed(1) + 'px, ' + moveY.toFixed(1) + 'px) scale(' + grow.toFixed(4) + ')';
      cell.style.backgroundColor = box.colour;
      cell.classList.add('is-fitted');

      // The enlarged photo is drawn bigger than the file the browser picked for a contained one, so ask for the file
      // that fits its real size; otherwise a bottle at twice the size is twice as soft on a phone.
      var wanted = Math.ceil(frameW * grow) + 'px';
      if (grow > 1.05 && image.getAttribute('sizes') !== wanted) image.setAttribute('sizes', wanted);
    }
  };

  /**
   * IS THE PHOTO BEHIND THE HEADER LIGHT OR DARK? Randy 2026-09-25: the logo
   * "just white, and on white image, black logo". A white packshot and a dark
   * lifestyle shot both lead galleries here, so the header's colour has to
   * come from the photo, and that is a FACT about pixels, read once per photo:
   * the top fifth of the active image (the band the header sits over), drawn
   * into a 24x6 canvas, averaged. Light above 0.7 of full white. The result
   * is one class on <html>, `pdp-light-photo`, which the stylesheet reads for
   * the logo and the icon colour. Cached per image, so a swipe back costs
   * nothing. A cross-origin image would taint the canvas; the images are on
   * the shop's own host.
   *
   * BLACK UNTIL PROVEN DARK (Randy's phone 2026-10-01, a white packshot with
   * white icons and a white logo: "white on white, shouldn't be possible").
   * White is only right over a photo that was READ and found dark. Every
   * path that ends without a reading - the image not loaded yet, a canvas
   * that throws, a canvas that comes back empty - writes the light state, and
   * `sections/header.liquid` writes it before this script runs. An unread
   * photo costs a black logo over a dark photo for a moment; the other way
   * round it cost an invisible header. Transparent pixels are skipped: an
   * empty canvas read as black, which is the darkest photo there is.
   */
  var TONE_LIGHT = 0.7;
  var toneCache = {};

  VerticalGallery.prototype.sampleTone = function (cell) {
    if (!this.isEnabled || !cell) return;
    var root = document.documentElement;
    var image = cell.querySelector(':scope > img') || cell.querySelector('video-media > img');
    if (!image) {
      root.classList.add('pdp-light-photo');
      return;
    }

    if (!image.complete || !image.naturalWidth) {
      root.classList.add('pdp-light-photo');
      this.awaitTone(cell);
      return;
    }

    var key = image.currentSrc || image.src;
    var light = toneCache[key];
    if (light === undefined) {
      try {
        var canvas = document.createElement('canvas');
        canvas.width = 24;
        canvas.height = 6;
        var context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, image.naturalWidth, Math.max(1, Math.round(image.naturalHeight * 0.2)), 0, 0, 24, 6);
        var data = context.getImageData(0, 0, 24, 6).data;
        var sum = 0;
        var seen = 0;
        for (var i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 8) continue;
          sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
          seen++;
        }
        if (!seen) {
          root.classList.add('pdp-light-photo');
          this.awaitTone(cell);
          return;
        }
        light = sum / seen > TONE_LIGHT;
        toneCache[key] = light;
      } catch (error) {
        root.classList.add('pdp-light-photo');
        this.awaitTone(cell);
        return;
      }
    }
    root.classList.toggle('pdp-light-photo', light);
  };

  /**
   * NEVER GIVE UP ON A PHOTO THAT WAS NOT READ (Gaetano 2026-10-05, his phone, a dark Gel-SD-Lyte: the logo stayed
   * black over the dark photo until the first scroll). The read above happened once per photo, and when the photo was
   * not in yet it waited for that one <img>'s own load. When that signal never arrived (the <img> swapped for a
   * sharper copy on the way, a canvas that came back empty on a slow phone) nothing read the photo again until the
   * visitor moved to another photo. Now an unread photo is read again on ANY image that lands inside its cell
   * (load does not bubble, but it does pass the cell in the capture phase), when the page has loaded, and a few times
   * shortly after, until there is an answer. Black stays the state while there is none, so white on white is still
   * impossible; the black moment is now short instead of lasting until a scroll.
   */
  VerticalGallery.prototype.awaitTone = function (cell) {
    if (cell.__bsmkToneWait) return;
    var self = this;
    var timers = [];
    var done = false;
    var settled = function () {
      var image = cell.querySelector(':scope > img') || cell.querySelector('video-media > img');
      return !!(image && toneCache[image.currentSrc || image.src] !== undefined);
    };
    var stop = function () {
      done = true;
      cell.__bsmkToneWait = false;
      cell.removeEventListener('load', retry, true);
      window.removeEventListener('load', retry);
      timers.forEach(clearTimeout);
    };
    var retry = function () {
      if (done) return;
      // Another photo is in front by now: that photo has its own reading; this one is read again if it comes back.
      if (self.toneCell !== cell || !self.isEnabled) {
        if (self.toneCell === cell) self.toneCell = null;
        stop();
        return;
      }
      // The wait flag stays up during the read, so a failed read does not start a second wait.
      self.sampleTone(cell);
      if (settled()) stop();
    };
    cell.__bsmkToneWait = true;
    cell.addEventListener('load', retry, true);
    if (document.readyState !== 'complete') window.addEventListener('load', retry, { once: true });
    [300, 1000, 2500, 5000].forEach(function (ms) { timers.push(setTimeout(retry, ms)); });
    // Still no answer after five seconds: stop trying, and let the next time this photo is confirmed read it afresh.
    timers.push(setTimeout(function () {
      if (done) return;
      stop();
      if (self.toneCell === cell) self.toneCell = null;
    }, 5200));
  };

  /**
   * FIVE DOTS AT MOST, SLIDING (Randy 2026-09-27, Instagram's indicator). The
   * box shows DOTS_WINDOW slots of DOTS_STEP px; the track slides so the active
   * dot sits near the middle, clamped at both ends; a slot at a window edge with
   * more dots beyond it is drawn small, the rest normal, the active one large.
   * Written from the index, so it is a fact about position, never a gesture.
   */
  var DOTS_WINDOW = 5;
  var DOTS_STEP = 16;

  VerticalGallery.prototype.sizeDots = function (dots, index) {
    var n = dots.length;
    var box = this.gallery.querySelector('.pdp-vgallery-dots');
    var track = this.gallery.querySelector('.pdp-vgallery-dots__track');
    if (!box || !track || !n) return;

    var start = n <= DOTS_WINDOW ? 0 : Math.max(0, Math.min(index - Math.floor(DOTS_WINDOW / 2), n - DOTS_WINDOW));
    var end = start + DOTS_WINDOW - 1;

    for (var i = 0; i < n; i++) {
      var size = '';
      if (i < start || i > end) size = 'out';
      else if ((i === start && start > 0) || (i === end && end < n - 1)) size = 'edge';
      if (i === index) size = '';
      dots[i].setAttribute('data-size', size);
    }

    box.style.setProperty('--pdp-dots-height', (Math.min(n, DOTS_WINDOW) * DOTS_STEP - (DOTS_STEP - 8)) + 'px');
    track.style.setProperty('--pdp-dots-shift', (-start * DOTS_STEP) + 'px');
  };

  VerticalGallery.prototype.setActive = function (index) {
    this.index = index;

    this.navGroups().forEach(function (buttons) {
      buttons.forEach(function (button, position) {
        button.setAttribute('aria-current', position === index ? 'true' : 'false');
      });
    });

    // The position dots. `data-current` rather than `aria-current` because they
    // are decorative and sit inside an aria-hidden container.
    var dots = this.gallery.querySelectorAll('.pdp-vgallery-dot:not([hidden])');
    for (var i = 0; i < dots.length; i++) {
      dots[i].setAttribute('data-current', i === index ? 'true' : 'false');
    }
    this.sizeDots(dots, index);

    /**
     * ONE PHOTO: THE PHOTO DOES NOT MOVE THE PAGE (Gaetano 2026-10-05, Jason Markk Protect Spray: "Dan wil je over de
     * foto vegen, maar dan trek je de hele lade er al overheen, terwijl dat niet kan"). With several photos a swipe on
     * the photo scrolls the photos and stops dead at the last one. With one photo there is nothing to scroll, so the
     * swipe went on to the page and pulled the panel over the photo. `pdp-vgallery--single` stops that in the
     * stylesheet (snippets/product-gallery.liquid): the panel is moved by the panel, as on every other product.
     */
    this.gallery.classList.toggle('pdp-vgallery--single', this.cells().length < 2);

    var cell = this.cells()[index];
    if (!cell) return;

    this.toneFromTop();

    // Keep anything else listening in step: the theme fires these two on a
    // horizontal settle and other components key off them.
    var detail = { detail: { index: index, cell: cell }, bubbles: false };
    this.carousel.dispatchEvent(new CustomEvent('carousel:change', detail));
    this.carousel.dispatchEvent(new CustomEvent('carousel:settle', detail));

    // carousel:settle is kept because it is what starts and stops video, but the
    // theme's adaptive-height listener rides on the same event. Undo its inline
    // sizing rather than dropping the event and losing video with it.
    this.clearAdaptiveHeight();
  };

  /**
   * Strip the inline height the theme's adaptive-height writes on settle.
   *
   * The stylesheet already wins this with `!important`, so this is tidiness
   * rather than the fix. It also handles the one case CSS cannot: a product left
   * with a single visible photo after variant filtering, where a fixed 4:5 frame
   * would leave dead space below it. No product has fewer than three photos
   * today (measured 2026-08-17), but filtering can still reduce it to one.
   */
  VerticalGallery.prototype.clearAdaptiveHeight = function () {
    this.carousel.style.removeProperty('max-height');

    if (this.cells().length < 2) {
      this.carousel.style.setProperty('height', 'auto', 'important');
    } else {
      this.carousel.style.removeProperty('height');
    }
  };


  /**
   * ==========================================================================
   * DESKTOP POSITION DOTS. Round 1.9, 2026-08-18.
   * ==========================================================================
   * Randy: "dots are missing in left side too (See miu miu example)."
   *
   * The dots exist in the markup at every width - this is only about desktop,
   * where the gallery is a STACK IN THE PAGE rather than a scroll container. Two
   * things follow from that and both are handled here:
   *
   *   1. **Sticky needs the dots ahead of the images.** As the last child they
   *      would only appear once the whole stack had scrolled past. Moved to the
   *      front, `position: sticky` pins them low-left for the length of the stack.
   *   2. **There is no scroll event to read.** On the phone the container's own
   *      scroll drives the active dot. Here the page scrolls, so the active image
   *      is whichever one covers most of the screen - an IntersectionObserver,
   *      which is also cheaper than a scroll handler.
   *
   * `1000px` and not `700px`: below the theme's two-column breakpoint the gallery
   * is a horizontal carousel again and the theme's own controls apply.
   *
   * The dots stay decorative. No click handler, `pointer-events: none` in the
   * stylesheet, `aria-hidden` in the markup - the photograph underneath is
   * already the tap target, and a second one over it is click surface the
   * tracking gate would then have to account for.
   */
  function DesktopDots(gallery) {
    this.gallery = gallery;
    this.dots = gallery.querySelector('.pdp-vgallery-dots');
    this.list = gallery.querySelector('.product-gallery__image-list');
    if (!this.dots || !this.list) return;

    this.ratios = [];
    this.enabled = false;
    this.mediaQuery = window.matchMedia(DESKTOP);
    this.mediaQuery.addEventListener('change', this.sync.bind(this));
    this.sync();

    this.ready = true;
  }

  DesktopDots.prototype.cells = function () {
    return Array.prototype.filter.call(
      this.gallery.querySelectorAll('.product-gallery__media'),
      function (cell) {
        return !cell.hasAttribute('hidden');
      }
    );
  };

  DesktopDots.prototype.sync = function () {
    if (this.mediaQuery.matches) this.enable();
    else this.disable();
  };

  DesktopDots.prototype.enable = function () {
    if (this.enabled) return;
    this.enabled = true;

    // Ahead of the images, or sticky has nothing to hold it in view.
    if (this.list.firstElementChild !== this.dots) {
      this.list.insertBefore(this.dots, this.list.firstElementChild);
    }

    if (!window.IntersectionObserver) return;

    var self = this;
    var cells = this.cells();
    this.ratios = cells.map(function () {
      return 0;
    });

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var index = cells.indexOf(entry.target);
          if (index > -1) self.ratios[index] = entry.intersectionRatio;
        });

        // Most of the screen wins. A tie keeps the earlier image, so scrolling
        // across a boundary does not flicker back and forth.
        var best = 0;
        var bestRatio = -1;
        for (var i = 0; i < self.ratios.length; i++) {
          if (self.ratios[i] > bestRatio + 0.001) {
            bestRatio = self.ratios[i];
            best = i;
          }
        }
        self.setActive(best);
      },
      { threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.9, 1] }
    );

    cells.forEach(function (cell) {
      self.observer.observe(cell);
    });
  };

  DesktopDots.prototype.disable = function () {
    if (!this.enabled) return;
    this.enabled = false;
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  DesktopDots.prototype.setActive = function (index) {
    var dots = this.dots.querySelectorAll('.pdp-vgallery-dot:not([hidden])');
    for (var i = 0; i < dots.length; i++) {
      dots[i].setAttribute('data-current', i === index ? 'true' : 'false');
    }
  };

  /**
   * ------------------------------------------------------------------------
   * MVP ITEM 5 - THE SIZE DRAWER
   * ------------------------------------------------------------------------
   *
   * State 1 is name, price, colour dots and one button. The size grid sits
   * collapsed behind "Kies je maat"; choosing a size turns the button into
   * "In winkelwagen - 43".
   *
   * WHY THE BUTTON IS INTERCEPTED RATHER THAN THE VARIANT DESELECTED
   * Shopify always has a selected variant (`selected_or_first_available_variant`),
   * so a true "no size chosen" state does not exist in the theme's model.
   * Deselecting would break the buy form. Instead the form keeps its preselected
   * variant and we simply refuse to submit until the visitor has picked a size
   * themselves.
   *
   * That is worth more than tidiness on a shoe shop: a preselected size means
   * add-to-carts in a size nobody chose, and those come back as returns, which
   * is basis-3 money. Forcing an explicit choice is a returns move as much as a
   * conversion one.
   *
   * PROGRESSIVE ENHANCEMENT. Everything here is additive. With JavaScript off
   * the grid is visible and the button submits exactly as it does today.
   */
  function SizeDrawer(section) {
    this.section = section;
    /**
     * ------------------------------------------------------------------------
     * NOTHING INSIDE `.product-info` IS HELD. IT IS LOOKED UP EVERY TIME.
     * ------------------------------------------------------------------------
     * `<product-rerender>` replaces the whole info column, so a node captured
     * here is detached from the first replacement onward - and writing to a
     * detached node fails SILENTLY rather than throwing.
     *
     * **This is the third bug from that one trap and it is why the reference is
     * gone rather than refreshed.** Found on the desktop round, 2026-08-18:
     * the buy button read "Kies je maat" on some loads and the theme's own
     * "Toevoegen aan winkelwagen" on others, on the same product with the same
     * code - two of four loads each way, at 1440x900. Desktop lost it more often
     * because it hydrates an express-checkout block the phone hides, and that
     * hydration is what triggers the re-render. **Mobile carried the same latent
     * bug and simply won the race**, so this fixes both.
     *
     * Only the SECTION is kept. That element is never replaced.
     */
    if (!section.querySelector('[data-block-id="variant_picker"]')) return;
    if (!this.buyBlock() || !this.mainButton()) return;

    this.hasChosenSize = false;
    this.pickedSize = '';

    /**
     * ONE SIZE: NOTHING TO CHOOSE, SO NOTHING TO WAIT FOR (Gaetano 2026-10-05, Jason Markk Protect Spray). A product
     * with one variant has no size buttons, so the visitor could never "choose a size": the button read "Kies je maat"
     * for good and every tap was caught below and sent to sizes that are not there. The product could not be added to
     * the cart from its own page, on the phone or on the desktop. Such a product counts as chosen from the start: the
     * button keeps the label the server wrote ("In winkelwagen", snippets/buy-buttons.liquid) and adds on the first
     * tap. Detected by the picker having no inputs, the same thing the visitor sees.
     */
    this.oneSize = !section.querySelector('[data-block-id="variant_picker"] input');
    if (this.oneSize) this.hasChosenSize = true;

    /**
     * ------------------------------------------------------------------------
     * NO BREAKPOINT HERE ANY MORE. 2026-08-18 evening, round 1.9.
     * ------------------------------------------------------------------------
     * Randy: "I simply want it to look like the mobile version, so everything
     * that still is on desktop should change."
     *
     * Every check in this object used to be gated on `(max-width: 999px)`,
     * because desktop was the control's page and its button had to keep the
     * theme's own label and the theme's own preselected variant. Desktop now
     * carries the same design, so all five gates are gone: the label, the stock
     * line in the heading, the sold-out waitlist swap, the sold-out CTA and the
     * refusal to add an unchosen size.
     *
     * **The gallery keeps its breakpoint.** `VerticalGallery` above is still
     * mobile-only - the vertical scroll container is a phone mechanism and
     * desktop uses the theme's own stacked grid.
     */
    this.bind();
    this.sync();

    /**
     * ------------------------------------------------------------------------
     * THE FIRST LABEL HAS TO BE WRITTEN MORE THAN ONCE, AND THAT IS A REAL BUG
     * FOUND ON DESKTOP. 2026-08-18 evening.
     * ------------------------------------------------------------------------
     * Measured on the challenger at 1440x900: the button read "Toevoegen aan
     * winkelwagen" on load roughly half the time, and "Kies je maat" the other
     * half, on the same product with the same code. Mobile got it right every
     * time. A label that is sometimes right is worse than one that is always
     * wrong, because the click interception is tied to it - the button would say
     * "add to cart" while refusing to add anything.
     *
     * **The cause is a race, not a breakpoint.** `setLabel` writes through a
     * 120ms crossfade timer, and the theme upgrades the buy form after
     * DOMContentLoaded - accelerated checkout hydrates and the button is swapped.
     * When the swap lands inside those 120ms our write goes to a node that is
     * already detached, and a detached node fails silently. Desktop lost the race
     * more often because it hydrates an express-checkout block the phone hides.
     *
     * So the opening pass is spread the same way the post-rerender one is, and
     * `load` gets a final one because images are the slowest thing on the page
     * and hydration is done by the time they finish. `setLabel` returns early
     * when the text is already right, so every extra pass costs one string
     * comparison and cannot loop.
     */
    this.renderSoon();
    window.addEventListener('load', this.renderSoon.bind(this), { once: true });

    this.ready = true;
  }

  /** Re-resolved on every call. See the constructor for why it is not held. */
  SizeDrawer.prototype.buyBlock = function () {
    return this.section.querySelector('[data-block-id="buy_buttons"]');
  };

  /** The add-to-cart button as it exists right now, or null. */
  SizeDrawer.prototype.mainButton = function () {
    var block = this.buyBlock();
    return block ? block.querySelector('button[type="submit"], button:not([type])') : null;
  };

  SizeDrawer.prototype.bind = function () {
    var self = this;

    /**
     * A BUTTON READING "KIES JE MAAT" MAY NEVER ADD ANYTHING TO THE CART.
     *
     * **This was gated on the drawer being closed and that was a real defect**,
     * found 2026-08-18 while wiring the theme's bar. Once the photos had scrolled
     * away the interception stopped, while the label still said "Kies je maat"
     * because no size had been picked. So the button said "choose your size" and
     * quietly added Shopify's preselected variant instead. On a sneaker shop that
     * is a return, which is basis-3 money.
     *
     * The condition is now the same one the LABEL uses - has the visitor picked a
     * size - so what the button says and what it does can no longer disagree.
     * It covers both buttons: the one in the page and the one in the sticky bar.
     */
    this.section.addEventListener(
      'click',
      function (event) {
        // Every width. A button reading "Kies je maat" may not add a size the
        // visitor never picked, and that is as true on a laptop as on a phone.
        if (self.hasChosenSize) return;

        var hit = self.buttons().some(function (button) {
          return button === event.target || button.contains(event.target);
        });
        if (!hit) return;

        // Do not add to cart yet. Take them to the sizes.
        event.preventDefault();
        event.stopPropagation();
        self.goToSizes();
      },
      true
    );

    // <product-rerender> replaces the buy button after a variant change, wiping
    // our label. It fetches, so it lands AFTER the change handler below. Re-apply
    // once the theme has finished. Without this the button reverts to
    // "Toevoegen aan winkelwagen" the moment a size is picked.
    document.addEventListener(
      'product:rerender',
      function () {
        self.renderSoon();
      },
      true
    );

    // A SIZE CHANGE LEAVES UNCHANGED BLOCKS ALONE (2026-10-06, Gaetano's recording: with the drawer open, a size
    // tap made the colour dots next to the price pop out again at the top right, and the colourway thumbnails went
    // blank for a frame). The theme's <product-rerender> swaps every "liquid" block on every variant change, whether
    // its markup changed or not; a fresh block means fresh images to decode and fresh CSS animations from their
    // start. This runs on the capture phase, so before the theme's own listener on the form, and takes out of the
    // fetched fragment every liquid block whose markup is identical to the one on the page; the theme then finds no
    // match for it and leaves the live block as it is. A block that really changed is still replaced.
    document.addEventListener(
      'product:rerender',
      function (event) {
        var fragment = event.detail && event.detail.htmlFragment;
        if (!fragment || !fragment.querySelectorAll || (event.detail && event.detail.productChange)) return;
        Array.prototype.forEach.call(
          fragment.querySelectorAll('[data-block-type="liquid"][data-block-id]'),
          function (block) {
            var live = self.section.querySelector('[data-block-type="liquid"][data-block-id="' + block.getAttribute('data-block-id') + '"]');
            if (live && live.innerHTML === block.innerHTML) block.remove();
          }
        );
      },
      true
    );

    // Takes priority over the state-1 handler above, which would otherwise send
    // a sold-out visitor to a size grid where nothing is selectable.
    this.section.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest('.pdp-cta--waitlist');
        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();

        var block = self.waitlistBlock();
        if (!block) return;

        block.scrollIntoView({ block: 'center', behavior: 'smooth' });

        // The app builds its field client side, so focus after the scroll has
        // had time to land rather than in the same tick.
        setTimeout(function () {
          var field = block.querySelector('input, button');
          if (!field) return;
          try { field.focus({ preventScroll: true }); } catch (error) {}
        }, 450);
      },
      true
    );

    // Round 2: the panel's handle and the "+N" of the colourway cluster both live
    // inside `.product-info`, which <product-rerender> replaces, so they are
    // delegated on the section rather than bound to the element. Both mean
    // "take me to the sizes", the same gesture as the button before a size.
    this.section.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest('.pdp-panel-handle, .pdp-colour-cluster__more');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        self.goToSizes();
      },
      true
    );

    window.addEventListener('scroll', function () { self.sync(); }, { passive: true });

    /**
     * ------------------------------------------------------------------------
     * A SIZE COUNTS AS CHOSEN ON THE CLICK, NOT ON THE CHANGE EVENT
     * ------------------------------------------------------------------------
     *
     * **This was a real defect and Randy found it: "I now load the page and
     * select size 41.5, it doesn't work, but if I do pick 42, it changes and I
     * can add to cart."**
     *
     * Shopify preselects `selected_or_first_available_variant`, which is the
     * FIRST AVAILABLE SIZE - 41.5 on that product. The radio is therefore already
     * checked when the page loads, and clicking it fires no `change` event at all,
     * because nothing changed. Every other size worked, which is exactly the shape
     * of the bug he described.
     *
     * The visitor's intent is the CLICK, so that is what this listens for. The
     * `change` handler stays for the keyboard path, where a size is reached with
     * the arrow keys and never clicked.
     *
     * Both are delegated on the section rather than on the picker, because
     * `<product-rerender>` replaces the picker on every variant change and a
     * listener bound to it dies with it.
     */
    var chooseSize = function (event) {
      /**
       * THE PICKER IS RE-RESOLVED ON EVERY EVENT, NEVER HELD.
       *
       * `<product-rerender>` replaces this column on each variant change, so a
       * reference captured in the constructor points at a detached node from the
       * first size click onward - and `detached.contains(liveLabel)` is false, so
       * every later click was silently ignored. Same stale-reference bug that
       * killed the label observers earlier today, in a different place.
       *
       * It also has to be THIS section's picker: `product-quick-buy` renders a
       * second copy of the whole product info into the page.
       */
      var picker = self.section.querySelector('[data-block-id="variant_picker"]');
      if (!picker || !picker.contains(event.target)) return;

      /**
       * A CLICK ON A LABEL ARRIVES TWICE, AND THE SECOND ONE UNDID THE FIRST.
       *
       * Clicking a `<label for>` makes the browser fire a second, synthetic click
       * on the input it points at. So the deselect below ran on the label's click
       * and was immediately reversed by the input's - the size looked stuck.
       * Measured 2026-08-18.
       *
       * Clicks are therefore only read from the LABEL, and `change` only from the
       * input, so each gesture is counted once.
       */
      var label = event.target.closest('label');
      if (event.type === 'click' && !label) return;
      if (event.type === 'change' && !event.target.closest('input')) return;

      // A size tap, choosing or letting go, retires the lock before the theme
      // rebuilds the column (see `Lock`). Harmless when it is already retired.
      Lock.retire();

      /**
       * CLICKING THE CHOSEN SIZE AGAIN LETS IT GO.
       *
       * Randy 2026-08-18: "when unclicking, it appears again as available." A
       * radio cannot be unchecked by clicking it, so this is our own state
       * rather than the form's - exactly as it is on page load, where Shopify
       * has a variant selected and the page does not act as though the visitor
       * chose it. The form keeps its variant either way; only the button and the
       * colourway filter follow what the visitor actually did.
       */
      var clicked = label ? (label.textContent || '').replace(/\s+/g, ' ').trim() : '';

      if (event.type === 'click' && self.hasChosenSize && clicked && clicked === self.pickedSize) {
        self.hasChosenSize = false;
        self.pickedSize = '';
        self.section.classList.remove('pdp-has-size');
        self.filterColourways(null);
        self.renderSoon();
        return;
      }

      self.hasChosenSize = true;
      /**
       * THE SIZE THE VISITOR CLICKED IS REMEMBERED, NOT RE-READ.
       *
       * `chosenSize()` reads `input:checked` out of the picker, and the picker is
       * being replaced by `<product-rerender>` at exactly the moment this runs. A
       * read that lands mid-swap returns nothing, which showed up twice: the
       * button flashed the theme's own label, and clicking a size a second time
       * re-selected it instead of letting it go. What the visitor clicked cannot
       * go stale.
       */
      if (clicked) self.pickedSize = clicked;
      // The stock line ("Nog 1 op voorraad") only appears once the visitor has
      // picked a size themselves. Shopify's preselected variant does not count,
      // for the same reason the button refuses to submit on it.
      self.section.classList.add('pdp-has-size');
      self.filterColourways(clicked || self.chosenSize());
      self.renderSoon();
    };

    this.section.addEventListener('click', chooseSize);
    this.section.addEventListener('change', chooseSize);

    /**
     * THE BUTTON LABEL FLICKER, and why an event listener could not fix it.
     *
     * Randy 2026-08-18: "when I click a size, it's a bit buggy, doesn't
     * transition nicely from kies je maat to in winkelwagen."
     *
     * `<product-rerender>` fetches the section and REPLACES the buy button. So
     * between the size being picked and our label being re-applied, the theme's
     * own default ("Toevoegen aan winkelwagen") is painted. Re-applying on the
     * `product:rerender` event is a frame or more too late to stop that.
     *
     * An observer on the buy block fires in the same microtask as the
     * replacement, so the theme's label never reaches the screen.
     */
    /**
     * ------------------------------------------------------------------------
     * ONE OBSERVER, ON THE SECTION, BECAUSE EVERYTHING INSIDE IT GETS REPLACED
     * ------------------------------------------------------------------------
     *
     * `<product-rerender>` fetches the section on every variant change and swaps
     * elements in. **An observer bound to the buy block or to the sticky bar is
     * destroyed by the very swap it exists to catch**, which is what made the
     * labels revert: measured 2026-08-18, one size click left both buttons
     * reading the theme's own "Toevoegen aan winkelwagen" until the NEXT click
     * happened to re-run the pass.
     *
     * The section element itself is never replaced, so that is what gets
     * watched. It also covers the waitlist app, which draws its widget late and
     * used to need an observer of its own.
     *
     * This cannot feed itself: `setLabel` returns early when the text is already
     * right, so our own write produces one no-op pass and stops.
     */
    if (window.MutationObserver) {
      this.labelObserver = new MutationObserver(function () {
        self.renderSoon();
      });
      this.labelObserver.observe(this.section, { childList: true, subtree: true });
    }

    // Belt and braces for an app that writes its widget in one go rather than
    // in steps the observer above would see.
    setTimeout(function () { self.render(); }, 1500);
    setTimeout(function () { self.render(); }, 4000);

    // A colourway is a different PRODUCT, so tapping a swatch is a normal page
    // load. Browsers restore the previous scroll offset on those, which drops the
    // visitor half way down the new page instead of at its photos. Flag the jump
    // here and undo it on arrival. Assumed to be scroll restoration rather than
    // anything the theme does; the fix is the same either way.
    //
    // The "+X" handler that used to sit here went with the compact cluster
    // (Randy 2026-08-18: "let's not animate the variants"). One picker now, and
    // every swatch in it is an ordinary link to that colourway.
    this.section.addEventListener('click', function (event) {
      if (event.target.closest('[data-pdp-colour-link]')) {
        try { sessionStorage.setItem('bsPdpJumpToTop', '1'); } catch (e) {}
      }
    });
  };

  /**
   * Where "Kies je maat" takes you, and it is the only movement left in this file.
   *
   * The size grid sits directly above the button now, so this is a short hop
   * rather than a state change: a tap on a button that asks for a size puts the
   * sizes on screen instead of adding one nobody picked.
   */
  SizeDrawer.prototype.goToSizes = function () {
    /**
     * ------------------------------------------------------------------------
     * THE DIVIDER AND THE BUTTON ARE THE SAME GESTURE, SO THEY LAND IN THE SAME
     * PLACE. Randy 2026-08-20, from his phone.
     * ------------------------------------------------------------------------
     * "It's not full screen anymore, because I see the image at top, so the
     * divider is no longer the top thing to see."
     *
     * `block: 'center'` on the size grid stopped wherever the grid happened to
     * sit, which left a strip of photograph above the handle. **That is the exact
     * complaint the DIVIDER tap was fixed for on 2026-08-18**, and the two
     * gestures mean the same thing to the visitor, so they had no business
     * landing in different places.
     *
     * `goToBuyingBlock` is that one implementation, and it is CALLED rather than
     * copied so the header offset cannot drift between the two paths.
     *
     * GATED ON THE BUTTON REALLY BEING STICKY, which is only true below 700px.
     * Above it the info column is a `<safe-sticky>` and the size grid is already
     * on screen, so the early return further down is the right answer and hauling
     * the page to the handle would be the "moving a page that is already showing
     * what it is asked for" defect all over again.
     */
    var block = this.buyBlock();
    var gallery = this.section.querySelector('product-gallery');
    var vgallery = gallery && gallery.bijsmaakVerticalGallery;

    // Round 2: the target is the lock's second point (the panel up to the
    // button), read off the page by `lockTarget`.
    if (block && vgallery && vgallery.isEnabled && window.getComputedStyle(block).position === 'sticky') {
      var target = vgallery.lockTarget();
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      // Only ever DOWN to it. A visitor already past that point keeps their
      // place; hauling them back up would be a guess about what they meant.
      if (target !== null && y < target - 2) {
        vgallery.goToBuyingBlock();
        return;
      }
    }

    var picker = this.section.querySelector('[data-block-id="variant_picker"]');
    var target = picker || document.querySelector('.product-info') || this.section;

    /**
     * DO NOT MOVE A PAGE THAT IS ALREADY SHOWING WHAT IT IS BEING ASKED FOR.
     *
     * Added with the desktop round. On a laptop the info column is sticky and the
     * size grid is on screen at the moment the button is clicked, so scrolling it
     * to the centre would jerk the page for no reason - the exact class of
     * "decide something for the visitor" that cost this build ten mechanisms.
     * On a phone the grid is genuinely below the fold and the hop still happens.
     */
    var box = target.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    if (box.top >= 0 && box.bottom <= viewport) return;

    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  /**
   * ------------------------------------------------------------------------
   * CROSS OUT THE COLOURWAYS THAT DO NOT COME IN THE SIZE YOU PICKED
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18: "can we show which variants are available once someone
   * clicks a size... Then a user can click size and see which variants are still
   * available."
   *
   * Each swatch carries its own in-stock sizes in `data-sizes`, written by
   * `snippets/pdp-colour-swatch.liquid` at render time, so this costs no request
   * and cannot go stale against the page it is on. The strike is the same
   * diagonal the theme puts on a sold-out size, so "cannot buy this" looks the
   * same wherever it appears.
   *
   * A swatch with no `data-sizes` at all is left alone rather than crossed out:
   * silence is not evidence of absence.
   */
  SizeDrawer.prototype.filterColourways = function (size) {
    var swatches = this.section.querySelectorAll('.pdp-colour-dot[data-sizes]');

    for (var i = 0; i < swatches.length; i++) {
      var sizes = swatches[i].getAttribute('data-sizes') || '';
      var unavailable = !!size && sizes.length > 1 && sizes.indexOf('|' + size + '|') === -1;
      swatches[i].classList.toggle('pdp-colour-dot--unavailable', unavailable);
    }
  };

  /** The size the visitor actually chose, for the button label. */
  SizeDrawer.prototype.chosenSize = function () {
    var picker = this.section.querySelector('[data-block-id="variant_picker"]');
    if (!picker) return '';
    var checked = picker.querySelector('input:checked');
    if (!checked) return '';
    var label = picker.querySelector('label[for="' + checked.id + '"]');
    var text = (label ? label.textContent : checked.value) || '';
    return text.replace(/\s+/g, ' ').trim();
  };

  /**
   * ONE BUY BUTTON ON A PHONE, AND IT IS THE ONE IN THE PAGE.
   *
   * The theme's own sticky bar was wired up here for one round and then dropped
   * (Randy 2026-08-18: "remove the sticky CTA please, it's too messy... don't use
   * the theme one"). It is hidden at every width in the stylesheet now: on a phone
   * because Randy asked, on a laptop because `<safe-sticky>` keeps the real button
   * on screen for the whole page once the info column fits. Nothing here touches
   * it either way.
   */
  SizeDrawer.prototype.buttons = function () {
    var main = this.mainButton();
    return main ? [main] : [];
  };

  /**
   * Swap the label through a short dip in opacity rather than in one frame.
   *
   * Returning early when the text is already right is what keeps the
   * MutationObserver above from feeding itself: our own write is a mutation, it
   * calls render() again, and the second pass finds nothing to do.
   *
   * The timer hangs off the BUTTON rather than off the drawer, because there are
   * two of them now and one shared timer would cancel the other's swap.
   */
  SizeDrawer.prototype.setLabel = function (button, text) {
    if (button.textContent.trim() === text) return;

    var section = this.section;
    section.classList.add('pdp-cta-swapping');

    clearTimeout(button.bijsmaakSwapTimer);
    button.bijsmaakSwapTimer = setTimeout(function () {
      button.textContent = text;
      section.classList.remove('pdp-cta-swapping');
    }, 120);
  };

  /** The waitlist app block, which is what a sold-out product has to offer. */
  SizeDrawer.prototype.waitlistBlock = function () {
    return this.section.querySelector('[data-block-id*="waitlist"]');
  };

  /**
   * Is there actually a waitlist to send anyone to?
   *
   * MEASURED 2026-08-18 on `new-balance-1906r-outerspace-black`, a fully sold
   * out product: the app block IS on the page, with 92 elements and 2.5KB of
   * text in it, and it renders at a height of ZERO with no input, no button and
   * no form. It is all scripts and styles; the widget itself never appears. The
   * app is collection-gated (`miniVacNotifyCollections` carries one id) and this
   * product is not in that collection.
   *
   * So the presence of the block proves nothing, and a CTA reading "Hou me op de
   * hoogte" over an empty div is worse than one reading "Uitverkocht". The label
   * only appears where the widget really rendered, and it starts working by
   * itself the day the app is switched on for these products.
   */
  SizeDrawer.prototype.hasWaitlist = function () {
    var block = this.waitlistBlock();
    if (!block) return false;
    if (block.querySelector('input, form')) return true;
    return block.offsetHeight > 0;
  };

  /**
   * Is anything at all buyable on this product?
   *
   * NOT the same question as "is the button disabled". The theme disables the
   * button whenever the SELECTED variant is unavailable, which happens on a
   * product where other sizes are in stock. Relabelling on that would tell a
   * visitor a shoe is gone while five sizes of it sit on the shelf.
   */
  SizeDrawer.prototype.anySizeAvailable = function () {
    var picker = this.section.querySelector('[data-block-id="variant_picker"]');
    if (!picker) return false;
    return !!picker.querySelector('label.block-swatch:not(.is-disabled)');
  };

  SizeDrawer.prototype.render = function () {
    var self = this;
    this.buttons().forEach(function (button) {
      self.renderButton(button);
    });
    this.renderSoldOutSize();
    this.renderSizeHeading();

    /**
     * EXPRESS CHECKOUT IS GATED ON THE SIZE, NOT ON THE PIN, AND THAT IS THE
     * WHOLE POINT. Randy 2026-08-26: "the shop only should show once people are
     * on the size flow etc. section."
     *
     * Same flag as the stock line, for the same reason: Shopify's preselected
     * variant is not a choice the visitor made.
     *
     * Tying it to the pin state would loop. Showing the button makes the buy
     * block taller, which moves the edge `CtaBand` measures to decide whether it
     * is pinned, which would hide it again. A measurement that changes what it
     * measures is the failure mode this file lost ten mechanisms to on 18 aug.
     */
    this.section.classList.toggle('pdp-size-chosen', this.hasChosenSize);

    // <product-rerender> rebuilds this column on every variant change, so the
    // crossed-out colourways have to be re-applied rather than assumed to hold.
    this.filterColourways(this.hasChosenSize ? (this.pickedSize || this.chosenSize()) : null);
  };

  /**
   * ------------------------------------------------------------------------
   * "KIES JE MAAT - NOG 1 OP VOORRAAD", ON ONE LINE
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18: "add 'nog X op voorraad' after 'kies je maat' so we have
   * more room. So it becomes 'kies je maat - nog 1 op voorraad' or 'niet op
   * voorraad' in orange/red."
   *
   * **The words come from the theme's own inventory block**, which is hidden on
   * mobile and read rather than replaced - so the threshold that decides when a
   * count is worth showing, and the wording it shows, stay the theme's. Ours is
   * only the position and the colour.
   *
   * Nothing is appended until the visitor has picked a size themselves, for the
   * same reason the button refuses to submit on Shopify's preselected variant.
   */
  SizeDrawer.prototype.renderSizeHeading = function () {
    var heading = this.section.querySelector('.pdp-size-heading');
    if (!heading) return;

    if (!heading.bijsmaakBaseText) {
      heading.bijsmaakBaseText = (heading.textContent || 'Kies je maat')
        .split(' - ')[0]
        .trim();
    }

    var suffix = '';

    if (this.hasChosenSize) {
      var button = this.mainButton();
      var soldOut = !!button && button.disabled;

      if (soldOut) {
        suffix = 'niet op voorraad';
      } else {
        var stock = this.section.querySelector('[data-block-id="inventory_qQ3T9A"]');
        var text = stock ? (stock.textContent || '').replace(/\s+/g, ' ').trim() : '';
        // The theme capitalises it as a sentence of its own; here it continues one.
        if (text) suffix = text.charAt(0).toLowerCase() + text.slice(1);
      }
    }

    var wanted = heading.bijsmaakBaseText + (suffix ? ' - ' : '');
    if (heading.firstChild && heading.firstChild.nodeType === 3) {
      if (heading.firstChild.nodeValue !== wanted) heading.firstChild.nodeValue = wanted;
    } else {
      heading.textContent = wanted;
    }

    var slot = heading.querySelector('.pdp-size-heading__stock');
    if (!suffix) {
      if (slot) slot.remove();
      return;
    }
    if (!slot) {
      slot = document.createElement('span');
      slot.className = 'pdp-size-heading__stock';
      heading.appendChild(slot);
    }
    if (slot.textContent !== suffix) slot.textContent = suffix;
  };

  /**
   * ------------------------------------------------------------------------
   * A SOLD-OUT SIZE OFFERS THE NOTIFICATION, NOT A DEAD BUTTON
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18: "when selecting out of stock size; the block with 'ontvang
   * een melding' that already exists below it, should appear there instead of
   * 'uitverkocht'. now it's below the fold so no one knows it exists."
   *
   * The app's block is moved rather than copied, so the widget it has already
   * drawn keeps working, and it is moved back the moment an available size is
   * chosen. `<product-rerender>` rebuilds this column on every variant change,
   * which is exactly when this runs, so the position is re-applied each time
   * rather than assumed.
   *
   * **Gated on the form really being there.** The app is collection-gated and
   * renders an empty block on most products, and hiding the buy button in favour
   * of nothing at all would be worse than the dead button it replaces.
   */
  SizeDrawer.prototype.renderSoldOutSize = function () {
    var button = this.mainButton();
    var waitlist = this.waitlistBlock();
    var soldOut = !!button && button.disabled && !!waitlist && this.hasWaitlist();

    this.section.classList.toggle('pdp-size-soldout', soldOut);
    if (!waitlist) return;

    if (soldOut) {
      // Remember where it belongs before taking it out of the flow.
      if (!waitlist.bijsmaakHome && waitlist.parentElement) {
        waitlist.bijsmaakHome = {
          parent: waitlist.parentElement,
          next: waitlist.nextElementSibling
        };
      }
      var block = this.buyBlock();
      if (block && block.nextElementSibling !== waitlist) {
        block.after(waitlist);
      }
    } else if (waitlist.bijsmaakHome && waitlist.parentElement) {
      var home = waitlist.bijsmaakHome;
      if (home.parent.isConnected && waitlist.nextElementSibling !== home.next) {
        home.parent.insertBefore(waitlist, home.next);
      }
    }
  };

  /**
   * RE-APPLY THE LABEL A FEW TIMES, BECAUSE A RE-RENDER IS A FETCH.
   *
   * `<product-rerender>` fetches the section and swaps the element in when the
   * response lands. A single pass in reaction to the swap can therefore run
   * against a node that is about to be discarded, and the theme's own label wins
   * by default. Measured 2026-08-18: after the first size click the block button
   * read "In winkelwagen - 40" while the bar still read "Toevoegen aan
   * winkelwagen", and it only corrected itself on the NEXT size click.
   *
   * Four passes over 900ms fixes it without any timing assumption: `setLabel`
   * returns early when the text is already right, so the extra passes cost a
   * string comparison and cannot loop. The fourth was added with the desktop
   * round, where the express-checkout block hydrates later than any of the first
   * three - see the constructor.
   */
  SizeDrawer.prototype.renderSoon = function () {
    var self = this;
    [0, 150, 400, 900].forEach(function (delay) {
      setTimeout(function () {
        self.render();
      }, delay);
    });
  };

  SizeDrawer.prototype.renderButton = function (button) {
    if (!button) return;
    // Per button: the bar's default label and the block button's are not
    // necessarily the same string, and neither is ours to guess at.
    if (button.bijsmaakOriginalLabel == null) {
      button.bijsmaakOriginalLabel = button.textContent.trim();
    }
    var originalLabel = button.bijsmaakOriginalLabel;

    /**
     * SOLD OUT IS AN OFFER, NOT A DEAD END.
     *
     * Randy 2026-08-18: the sold-out CTA reads "Uitverkocht - Herinner mij".
     * The waitlist app is already on the page and nobody scrolls to find it
     * under a button that says the shoe is gone. Naming both halves is the
     * honest version: the shoe is gone AND there is something you can do.
     *
     * Two conditions, and both matter. Only when the whole product is
     * unavailable, so a sold-out size on a live shoe still reads "Uitverkocht".
     * And only when the waitlist block actually rendered, so the button never
     * promises something the page cannot deliver. The third condition used to be
     * "mobile only" and went with the desktop round.
     *
     * `type="button"` is what makes enabling it safe: it can no longer submit
     * the form, whatever else happens.
     */
    if (button.disabled) {
      if (this.anySizeAvailable()) return;
      if (!this.hasWaitlist()) return;

      button.disabled = false;
      button.type = 'button';
      button.classList.add('pdp-cta--waitlist');
      this.setLabel(button, 'Uitverkocht - Herinner mij');
      return;
    }

    if (!this.hasChosenSize) {
      this.setLabel(button, 'Kies je maat');
      return;
    }

    var size = this.pickedSize || this.chosenSize();
    this.setLabel(button, size ? 'In winkelwagen - ' + size : originalLabel);
  };

  /**
   * THE STATE IS THE SCROLL POSITION. Not a click, not a latch.
   *
   * Randy 2026-08-18: "Kies je maat should ONLY show when we're at top of page...
   * once clicked OR scrolled you go to the full screen section, where the sizes
   * are ALWAYS shown. So never an extra click when people are on the full screen
   * next section."
   *
   * So state 1 (photos on screen) collapses the grid and the button is a way in.
   * State 2 (photos gone) always shows the grid, and the button is a real
   * add-to-cart. Scrolling back up returns to state 1 on its own.
   *
   * The earlier version latched open on first click, which meant that once a
   * visitor had opened it they were in state 2 forever, even back at the photos.
   */
  /**
   * ------------------------------------------------------------------------
   * NOTHING ON THIS PAGE DEPENDS ON THE SCROLL POSITION ANY MORE
   * ------------------------------------------------------------------------
   *
   * Randy 2026-08-18, closing round 1: "So not multiple buttons, states, etc.
   * Just 1 PDP with all in it."
   *
   * What used to live here, in order: an eased page scroll on touchend, a
   * direction-tracking snap, a CSS snap barrier gated on a class, two
   * `scroll-snap-stop` points, a momentum hand-off, a two-layout colour picker, a
   * white band that followed a pinned button, and finally a collapse that held the
   * size grid back until the photos left the screen. **Every one of them was
   * built, measured and then removed inside the same two days.**
   *
   * The reason is the same each time and it is worth keeping written down: each
   * had to decide something on the visitor's behalf - which way they meant to go,
   * whether they were done, what they wanted to see next - and a wrong guess costs
   * more than the mechanism ever won.
   *
   * And since round 1.9 not even a breakpoint check is left: desktop carries the
   * same design, so the label logic runs at every width.
   */
  SizeDrawer.prototype.sync = function () {
    this.render();
  };

  /**
   * ----------------------------------------------------------------------------
   * A FAILED BUILD IS NEVER CACHED, AND THAT WAS A SIX-SECOND BUG
   * ----------------------------------------------------------------------------
   * `init()` runs on every DOM mutation while the page is still parsing. So it
   * regularly meets a section whose variant picker has been parsed and whose buy
   * button has not - the selector matches, the constructor bails on the missing
   * button, and the half-built object was stored on the element anyway. Every
   * later pass then hit `if (section.bijsmaakSizeDrawer) return` and did nothing,
   * for the life of the page.
   *
   * MEASURED on the challenger at 1440x900, 2026-08-18: the buy button read the
   * theme's own "Toevoegen aan winkelwagen" for the first six seconds and only
   * then became "Kies je maat". **Those six seconds were the dangerous state**,
   * because the click interception was already live - a button that says "add to
   * cart" and refuses to add anything. It looked like a random 50/50 across loads
   * because it depends on where the parser happens to be when a mutation fires.
   *
   * The same trap was one line above it for the gallery. Both objects now report
   * `ready`, and only a ready one is stored, so an unfinished section is simply
   * retried on the next mutation.
   */
  /**
   * ---------------------------------------------------------------------------
   * THE WHITE BAND UNDER THE PINNED BUY BUTTON
   * ---------------------------------------------------------------------------
   * Randy 2026-08-20: "if the white band isn't too difficult, add it back too."
   *
   * The buy button is pinned 44px off the bottom of the screen so it clears
   * Safari's floating bar. The page keeps flowing through that 44px, so the top
   * of the size grid appears in the gap. The band paints over it, and the CSS
   * that draws it is in `snippets/product-gallery.liquid`.
   *
   * **IT BELONGS TO THE LANDING, NOT TO THE PIN.** Randy 2026-08-20: "only make
   * it white at top, and as soon as someone scrolls a bit, it gets removed."
   * Tying it to the pin kept it painted for the whole 320px of scroll before the
   * button releases, which is most of the way into the size grid. So the band is
   * on at the top of the page and off `TOP_BAND_PX` further down, and the CSS
   * fades it rather than switching it.
   *
   * **WHY THIS IS A SCRIPT AND NOT A RULE.** CSS cannot ask how far the page has
   * scrolled, and it cannot ask whether a `position: sticky` element is stuck
   * either - there is no `:stuck` selector. A band painted unconditionally would
   * cover the bottom of every screen for the whole length of the page.
   *
   * **IT MEASURES A FACT, IT DOES NOT GUESS AN INTENTION**, and that is the line
   * this file has to hold. The ten mechanisms deleted on 2026-08-18 each had to
   * decide what the visitor meant by a gesture. This one reads where the browser
   * has actually put the button and reports it. There is nothing to be wrong
   * about, and if the class were never written the page would still be correct,
   * only with the strip showing again.
   *
   * NOTHING INSIDE `.product-info` IS HELD. `<product-rerender>` replaces that
   * column on every variant change, so the block is looked up on every pass.
   * The class goes on the SECTION, which is never replaced.
   */
  function CtaBand(section) {
    this.section = section;
    if (!this.block()) return;

    this.pinned = null;
    this.frame = 0;

    var self = this;
    this.onScroll = function () {
      if (self.frame) return;
      self.frame = requestAnimationFrame(function () {
        self.frame = 0;
        self.sync();
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    window.addEventListener('orientationchange', this.onScroll);
    document.addEventListener('product:rerender', this.onScroll, true);
    // Images are the slowest thing on the page and the stack above the button
    // settles with them, so the pin line is only final once they are in.
    window.addEventListener('load', this.onScroll);

    this.sync();
    this.ready = true;
  }

  /** Re-resolved every pass. See the constructor for why it is not held. */
  CtaBand.prototype.block = function () {
    return this.section.querySelector('.product-info__block-item[data-block-id="buy_buttons"]');
  };

  CtaBand.prototype.set = function (shown) {
    if (shown === this.shown) return;
    this.shown = shown;
    this.section.classList.toggle('pdp-cta-band', shown);
  };

  /** A section replaced by the theme editor leaves its listeners behind. */
  CtaBand.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('orientationchange', this.onScroll);
    window.removeEventListener('load', this.onScroll);
    document.removeEventListener('product:rerender', this.onScroll, true);
  };

  CtaBand.prototype.sync = function () {
    if (!this.section.isConnected) return this.destroy();

    var block = this.block();
    if (!block) return this.set(false);

    /**
     * SELF-GATING ON THE BREAKPOINT, rather than repeating it here. The sticky
     * rule lives in `(max-width: 999px)`, so above it the computed position is
     * static and there is nothing to mask. Reading the used value means the day
     * that breakpoint moves, this follows it instead of contradicting it.
     */
    var style = window.getComputedStyle(block);
    if (style.position !== 'sticky') return this.set(false);

    var offset = parseFloat(style.bottom);
    if (isNaN(offset)) return this.set(false);

    var viewport = window.innerHeight || document.documentElement.clientHeight;
    var rect = block.getBoundingClientRect();

    /**
     * TWO CONDITIONS, AND BOTH ARE MEASUREMENTS.
     *
     * PINNED: the browser is holding the button at the pin line, so its bottom
     * edge sits exactly there. Released, its own place in the flow has risen
     * above that line and the edge reads SMALLER. One pixel of tolerance for
     * subpixel layout. This is what keeps the band off a page that opens part
     * way down, and off a sold-out variant whose button has no box at all.
     *
     * AT THE TOP: `TOP_BAND_PX` of scroll, so the band is a property of the
     * landing screen rather than of the pin. It is a threshold and not a taper
     * because the fade belongs in CSS, where it costs nothing and cannot stutter
     * against a scroll handler.
     */
    var pinned = rect.bottom >= viewport - offset - 1;
    // Round 3 (2026-10-03, snippets/bsmk-pdp-sheet.liquid): with the panel on, the landing screen is "the panel is
    // closed", not "the page is at its top" (open is the top of the page too since the rebuild of 3 Oct).
    var sheet = document.documentElement.classList;
    var atTop = sheet.contains('pdp-sheet-ready')
      ? !sheet.contains('pdp-sheet-open')
      : (window.pageYOffset || document.documentElement.scrollTop) <= TOP_BAND_PX;
    this.set(pinned && atTop);
  };

  /**
   * THE HEADER IS ABSENT ON LANDING AND COMES BACK ON A SCROLL UP.
   *
   * Randy 2026-08-25, pointing at Zara Home: "no header when you land, so only
   * when scrolling UP it shows, scrolling down won't. that gives us more room."
   *
   * **The room is the point.** The header is 57px of real flow space at the top
   * of the page, and taking it out moves the name, the price and the pinned
   * button up by exactly that. Measured across 75 renders: the button stopped
   * being clipped by the screen edge on every single size, and the price came
   * back on 402x662, which is 21.1% of mobile traffic.
   *
   * **THIS SCRIPT MEASURES A FACT, IT DOES NOT GUESS AN INTENTION.** That is the
   * line this file's ten deleted mechanisms failed on 2026-08-18. Scroll
   * direction is not an inference: the page either moved up or it did not. If
   * this never ran the header would simply stay hidden, which is a worse page
   * rather than a broken one.
   *
   * **The CSS lives in `snippets/product-gallery.liquid`, fenced inside
   * `max-width: 999px`.** Desktop is untouched, and `matchMedia` below keeps the
   * listener off desktop entirely rather than relying on the CSS alone.
   *
   * **A DEAD ZONE OF 4px AT THE TOP AND 2px EITHER WAY.** iOS rubber-banding
   * reports tiny scrolls in both directions at rest, and without the dead zone
   * the header flickers in and out while the page is standing still.
   */
  function PdpHeader() {
    var mobile = window.matchMedia('(max-width: 999px)');
    var root = document.documentElement;

    /**
     * ROUND 2, 2026-09-25: TWO CLASSES, BOTH FACTS ABOUT POSITION.
     *
     * `pdp-at-top`: the page is within TOP_BAND_PX of its top, the same line the
     * white band under the button uses. The stylesheet reads it twice: the
     * header is the glass look over the photo while it is set, and the compact
     * colourway cluster on the price line fades out when it is not. It is also
     * set inline in `sections/header.liquid` before the header is parsed, so
     * the first paint is already right.
     *
     * `pdp-header-hidden`: the page has travelled DOWN. The header is visible on
     * landing now (the photo runs under it, so it costs no room), hides on a
     * scroll down and comes back on a scroll up, in the solid look. Hidden only
     * applies away from the top, in the stylesheet, so the two can never fight.
     *
     * DIRECTION IS ACCUMULATED, NEVER READ FROM ONE EVENT (Randy 2026-08-25:
     * "sometimes when i scroll up and stop, the menu disappears"). iOS emits
     * small opposite-direction deltas as momentum settles and as the page
     * rubber-bands, so travel is summed, thrown away whenever the sign flips,
     * and the state only changes once 12px have accumulated ONE way.
     *
     * `pdp-lock` switches on the page-level scroll snapping (plan item 6). It
     * is never written in a headless browser: page-level snapping hangs the
     * measuring harness, and `navigator.webdriver` is a fact about the
     * environment, not a guess about a visitor.
     */
    var THRESHOLD = 12;
    var last = Math.max(0, window.scrollY || 0);
    var travel = 0;

    function sync() {
      if (!mobile.matches) {
        root.classList.remove('pdp-header-hidden', 'pdp-at-top', 'pdp-light-photo', 'pdp-over-photo');
        Lock.disarm();
        return;
      }

      // iOS reports a NEGATIVE scrollY while the page is bounced past the top.
      // Clamping keeps the bounce from registering as travel in either direction.
      var y = Math.max(0, window.scrollY || 0);
      // Round 3 (2026-10-03, snippets/bsmk-pdp-sheet.liquid): while the panel is on, "at the top" means the panel
      // is closed; since the rebuild of 3 Oct the open panel sits at the top of the page as well.
      root.classList.toggle('pdp-at-top', root.classList.contains('pdp-sheet-ready')
        ? !root.classList.contains('pdp-sheet-open')
        : y <= TOP_BAND_PX);

      // WHAT IS BEHIND THE HEADER: the held photo, or the white panel that has
      // slid up over it. The panel's top edge is a fact of layout; while it sits
      // below the middle of the header band, the photo is what shows through.
      // Re-resolved every time, because <product-rerender> replaces the column.
      var info = document.querySelector('.shopify-section--main-product .product-info');
      // Round 3 (2026-10-03, snippets/product-gallery.liquid): while the panel is open the photo strip is held
      // above it (`pdp-sheet-held`), so the photo is behind the bar even though the panel's edge has passed it.
      // Without this the class flipped off here and back on in the header's tone script on every frame.
      var overPhoto = root.classList.contains('pdp-sheet-held') ||
        (info ? info.getBoundingClientRect().top > OVER_PHOTO_PX : y <= TOP_BAND_PX);
      root.classList.toggle('pdp-over-photo', overPhoto);

      var delta = y - last;
      last = y;
      if (!delta) return;

      // While the page is being moved by a script (a tap on the handle, the
      // jump to the top after a colourway switch) or by the browser settling it,
      // the movement is not the visitor's, so it says nothing about what they
      // want from the header. Randy's recording of 2026-09-27 12:57: the header
      // blinked on and off while Safari dragged a freshly loaded page about.
      if (Lock.suspended) { travel = 0; return; }

      if ((delta > 0) !== (travel > 0)) travel = 0;
      travel += delta;

      if (travel <= -THRESHOLD) root.classList.remove('pdp-header-hidden');
      else if (travel >= THRESHOLD) root.classList.add('pdp-header-hidden');
    }

    sync();
    // The lock arms once the page has loaded and settled, never during the
    // landing: a snap engine that wakes while the browser is still placing the
    // page (scroll restoration, our own jump to the top after a colourway
    // switch, images landing) can pull it to a rest point nobody asked for.
    var armLater = function () { setTimeout(function () { Lock.arm(); }, 250); };
    if (document.readyState === 'complete') armLater();
    else window.addEventListener('load', armLater, { once: true });
    window.addEventListener('scroll', sync, { passive: true });

    // A width change can strand the classes on desktop, where the rules do not
    // apply and the header is sticky again.
    if (mobile.addEventListener) mobile.addEventListener('change', function () { sync(); Lock.arm(); });
  }

  /**
   * ==========================================================================
   * PRODUCT VIDEO: PLAYS SILENTLY WHEN IT SCROLLS INTO VIEW. 2026-09-03.
   * ==========================================================================
   * Gaetano, 2026-09-03: "het zou nice zijn als de video automatisch afspeelt
   * als je er doorheen scrolt", and it has to start muted. The gallery snippet
   * now renders every product video `muted`; this object does the playing.
   *
   * Why not the theme's own two paths:
   *   - `autoplay-media` on <product-gallery> plays on `carousel:settle`. Our
   *     vertical gallery fires that, so the phone would work, but the desktop
   *     gallery is a stack in the page and never settles, and nothing pauses a
   *     clip once the whole gallery has scrolled off the screen.
   *   - `autoplay` on <video-media> plays in view through motion's inView, never
   *     pauses on leave, and drops the poster image, so a slow connection shows
   *     a blank cell until the file arrives.
   *
   * One IntersectionObserver rooted on the viewport, both breakpoints. A
   * viewport root also honours the phone strip's own clipping, so a video half
   * hidden inside the scroll container reads as half visible. Plays at 50%
   * visible, pauses below it.
   *
   * It calls the theme element's play()/pause(), never the raw <video>. Those
   * set `loaded` (hides poster and play button), `playing` (pauses the rest of
   * the group), and turn a blocked autoplay (iOS Low Power Mode) into a
   * `suspended` state with the play button still there. The theme's pause() on
   * a clip that never started is a no-op, so it never creates the player and
   * never hides the poster by accident.
   *
   * External videos (YouTube, Vimeo) are left alone: play() on those loads a
   * third-party player, which is a cost the visitor did not ask for.
   *
   * `ready` is set on every exit, including "nothing to do": init() runs on
   * every DOM mutation and a gallery without a video should not be re-scanned
   * for the life of the page.
   */
  function VideoAutoplay(gallery) {
    this.gallery = gallery;
    this.ready = true;

    if (!window.IntersectionObserver) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var cells = gallery.querySelectorAll('.product-gallery__media[data-media-type="video"]');
    if (!cells.length) return;

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var media = entry.target.querySelector('video-media');
          if (!media || typeof media.play !== 'function') return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) media.play();
          else media.pause();
        });
      },
      { threshold: [0, 0.5] }
    );

    var self = this;
    cells.forEach(function (cell) {
      self.observer.observe(cell);
    });
  }

  function init(root) {
    if (!document.documentElement.bijsmaakPdpHeader) {
      document.documentElement.bijsmaakPdpHeader = true;
      PdpHeader();
    }

    (root || document).querySelectorAll('product-gallery').forEach(function (gallery) {
      if (!gallery.bijsmaakVerticalGallery) {
        var instance = new VerticalGallery(gallery);
        if (instance.ready) gallery.bijsmaakVerticalGallery = instance;
      }

      if (!gallery.bijsmaakDesktopDots) {
        var dots = new DesktopDots(gallery);
        if (dots.ready) gallery.bijsmaakDesktopDots = dots;
      }

      if (!gallery.bijsmaakVideoAutoplay) {
        var video = new VideoAutoplay(gallery);
        if (video.ready) gallery.bijsmaakVideoAutoplay = video;
      }
    });

    // The drawer lives on the section, not the gallery, because the size grid and
    // the buy button are siblings of the gallery rather than children of it.
    (root || document)
      .querySelectorAll('.shopify-section:has([data-block-id="variant_picker"])')
      .forEach(function (section) {
        if (!section.bijsmaakSizeDrawer) {
          var drawer = new SizeDrawer(section);
          if (drawer.ready) section.bijsmaakSizeDrawer = drawer;
        }

        // Its own object rather than a method on the drawer, so that a drawer
        // that bails on a half-parsed section cannot take the band with it.
        // init() runs on every mutation while the page is still parsing.
        if (!section.bijsmaakCtaBand) {
          var band = new CtaBand(section);
          if (band.ready) section.bijsmaakCtaBand = band;
        }
      });
  }

  // Landing after a colourway switch: start at the photos, not where the previous
  // page happened to be scrolled to.
  try {
    if (sessionStorage.getItem('bsPdpJumpToTop')) {
      sessionStorage.removeItem('bsPdpJumpToTop');
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      // Snapping stays off until the visitor's first own gesture on the new
      // page, so the jump to the top and the snap engine never meet.
      Lock.suspend();
      window.scrollTo(0, 0);
      window.addEventListener('load', function () { window.scrollTo(0, 0); }, { once: true });
    }
  } catch (e) {}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // The product template re-renders on variant change (<product-rerender>), and
  // the theme editor re-renders on every setting change. Both replace the
  // gallery element, so re-bind rather than assuming one pass is enough.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  new MutationObserver(function () {
    init();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
